/* eslint-disable no-undef */
/* eslint-disable camelcase */
/**
 * Page: Backups.
 */

import Helpers from '../helpers';
import Preflight from '../elements/pre-flight';
import ExportUI from '../components/progress/export';

/**
 * Helper collections.
 */
const Helper = {
	/**
	 * Checks if provided string is empty.
	 *
	 * @param {string} label
	 * @return {boolean} True if the provided value is empty else false.
	 */
	isEmpty( label ) {
		return ( '' === label || null === label || 'undefined' === label ) ? true : false;
	},

	/**
	 * Checks if the provided data is pure JS object.
	 *
	 * @param {*} obj
	 * @return {boolean} True|False.
	 */
	isObject( obj ) {
		if ( null === obj || 'undefined' === typeof obj ) {
			return false;
		}

		if ( 'object' === typeof obj && 'Object' === obj.constructor.name ) {
			return true;
		}

		return false;
	},

	/**
	 * Convert pure Object to FormData
	 *
	 * @param {Object} obj
	 * @return {FormData} FormData.
	 */
	objToFormData( obj ) {
		if ( this.isObject( obj ) ) {
			const formData = new FormData();
			for ( const key in obj ) {
				formData.append( key, obj[ key ] );
			}
			return formData;
		}

		return obj;
	},

	/**
	 * Appends the query strings to the url.
	 *
	 * @param {string} url
	 * @param {*}      args
	 *
	 * @return {string}
	 */
	buildUrl( url, args ) {
		if ( this.isEmpty( url ) ) {
			return '';
		}

		const params = new URLSearchParams();

		for ( const key in args ) {
			if ( ! this.isEmpty( args[ key ] ) ) {
				params.set( key, args[ key ] );
			}
		}

		return `${ url }?${ params.toString() }`;
	},

	/**
	 * Resolve or Reject the promise.
	 *
	 * @return {Promise} Returns promise interface.
	 */
	deferred() {
		let res,
			rej;

		const p = new Promise( ( a, b ) => ( res = a, rej = b ) );

		p.resolve = res;
		p.reject = rej;

		return p;
	},

	/**
	 * Sends the AJAX request.
	 *
	 * @param {Object} data
	 *
	 * @return {Promise}
	 */
	request( data ) {
		let method = 'POST';
		if ( 'method' in data && 'GET' === data.method ) {
			method = 'GET';
		}
		delete data.method;

		const args = {
			credentials: 'same-origin',
			method
		};

		let ajaxUrl = SnapshotAjax.ajaxurl;
		if ( 'GET' === method ) {
			ajaxUrl = this.buildUrl( ajaxUrl, data );
		} else {
			data = this.objToFormData( data );
			args.body = data;
		}

		return fetch( ajaxUrl, args );
	},

	/**
	 * Trigger the defined event.
	 *
	 * @param {string} event Name of the event.
	 * @param {*}      els   List of elements|element.
	 */
	trigger: ( event, els ) => {
		if ( ! els || Helper.isEmpty( event ) || els.length === 0 ) {
			return;
		}

		if ( els.length > 1 ) {
			els.forEach( ( el ) => {
				el.dispatchEvent( new Event( event ) );
			} );
		} else if ( els instanceof Element ) {
			els.dispatchEvent( new Event( event ) );
		}
	},
};

/**
 * UI object.
 */
const UI = {
	/**
	 * Snapshot backups page tabs.
	 */
	tabs: {
		backups: [ '.snapshot-list-backups', '.snapshot-vertical-backups' ],
		logs: [ '.snapshot-logs', '.snapshot-vertical-logs' ],
		settings: [ '.snapshot-backups-settings', '.snapshot-vertical-settings' ],
		notifications: [ '.snapshot-notifications', '.snapshot-vertical-notifications' ]
	},

	/**
	 * Switch the tab.
	 *
	 * @param {string} tab Id of the tab to switch to.
	 */
	switchTab( tab ) {
		if ( Helper.isEmpty( tab ) ) {
			return;
		}

		const $navTab = document.querySelector( this.tabs[ tab ][ 1 ] );
		const $tabEl = document.querySelector( this.tabs[ tab ][ 0 ] );

		for ( const name in this.tabs ) {
			if ( tab === name ) {
				$navTab.classList.add( 'current' );
				$tabEl.style.display = 'block';
			} else {
				document.querySelector( this.tabs[ name ][ 0 ] ).style.display = 'none';
				document.querySelector( this.tabs[ name ][ 1 ] ).classList.remove( 'current' );
			}
		}
	},

	/**
	 * Hide errors.
	 *
	 * @param {string} selector
	 */
	hideErrors( selector ) {
		const $els = document.querySelectorAll( selector );

		if ( $els.length > 0 ) {
			$els.forEach( ( $el ) => {
				if ( 'block' === $el.style.display ) {
					$el.style.display = 'none';
				}
			} );
		}
	},

	/**
	 * Display the notice.
	 *
	 * @param {*} args
	 */
	displayNotice( args ) {
		if ( ! ( 'message' in args ) || Helper.isEmpty( args.message ) ) {
			// Message must be set and mustn't be empty.
			return;
		}

		let icon_class = '';

		switch ( args.type ) {
			case 'success':
				icon_class = 'check-tick';
				break;

			case 'error':
			case 'warning':
				icon_class = 'warning-alert';
				break;

			case 'info':
			default:
				icon_class = 'info';
				break;
		}

		const options = {
			icon: icon_class,
			type: args.type
		};

		if ( args.dismissible ) {
			options.dismiss = { show: true };
		}

		if ( args.autoclose ) {
			options.autoclose = { timeout: args.autoclose };
		} else {
			options.autoclose = { show: false };
		}

		const $suiHeader = document.querySelector( '.sui-header' );
		const noticeId = 'snapshot-notice-' + Math.random().toString( 36 ).substr( 2, 9 );
		let $noticesWrapper = document.querySelector( '.sui-floating-notices' );
		const $notice = document.createElement( 'div' );

		if ( ! $noticesWrapper ) {
			$noticesWrapper = document.createElement( 'div' );
			$noticesWrapper.setAttribute( 'class', 'sui-floating-notices' );
		}
		$suiHeader.insertBefore( $noticesWrapper, $suiHeader.childNodes[ 0 ] );

		$notice.setAttribute( 'id', noticeId );
		$notice.setAttribute( 'role', 'alert' );
		$notice.setAttribute( 'aria-live', 'assertive' );
		$notice.setAttribute( 'class', 'sui-notice' );

		$noticesWrapper.appendChild( $notice );

		const noticeContent = `<p>${ args.message }</p>`;
		SUI.openNotice( noticeId, noticeContent, options );
	},

	/**
	 * Initialize the select2 dropdown for normal "Select"
	 *
	 * @param {HTMLSelectElement} $el
	 */
	initSelect2( $el ) {
		if ( 'nodeName' in $el && 'select' !== $el.nodeName.toLowerCase() ) {
			return;
		}

		SUI.select.init( jQuery( $el ) );

		// Add "onchange" event to the "select"
		$el.onchange = ( e ) => {
			Log.filter( e );
		};
	},

	/**
	 * Attach 'click' event to all the dynamically appended .log-row
	 *
	 * @param {Event} event Click event.
	 */
	tr_click( event ) {
		let el = event.target;

		if ( el.classList.contains( 'log-row' ) || el.parentNode.classList.contains( 'log-row' ) ) {
			if ( 'nodeName' in el && '' !== el.nodeName && 'tr' !== el.nodeName.toLowerCase() ) {
				el = el.parentNode;
				Log.single( el );
			}
		}
	},

	/**
	 * Build backups table.
	 *
	 * @param {*} data
	 * @param     response
	 */
	build_backups_table( response ) {
		const $page_main = document.querySelector( Log.main_page );
		const $backups = $page_main.querySelector( '.snapshot-listed-backups' );
		const $table = $backups.querySelector( 'table' );

		if ( response.backups !== undefined ) {
			const backups_number = response.backups.length;
			const failed_number = response.failed_backups || 0;

			$table.querySelector( 'tbody' ).innerHTML = '';

			const backups = response.backups;
			if ( backups && backups.length ) {
				const $tbody = document.querySelector( 'tbody' );
				backups.forEach( ( backup ) => {
					const row = backup.row;
					$tbody.innerHTML += backup.row;
					$tbody.innerHTML += backup.row_content;
				} );

				const trs = $tbody.querySelectorAll( 'tr.snapshot-row' );
				trs.forEach( ( tr ) => {
					tr.classList.add( 'sui-accordion-item' );
					const nextTr = tr.nextElementSibling;
					nextTr.querySelector( '.row-current-schedule' ).querySelector( '.sui-loading' ).style.display = 'none';
					nextTr.querySelector( '.open-edit-schedule' ).style.display = 'block';
					nextTr.querySelector( 'span.schedule' ).textContent = document.querySelector( '.snapshot-schedule-frequency' ).textContent;
					const openEditSchedule = nextTr.querySelector( '.open-edit-schedule' );
					openEditSchedule.onclick = ( e ) => {
						e.preventDefault();
						SUI.openModal( 'modal-snapshot-edit-schedule', e.target );
					};
				} );
			}
		}
	}
};

/**
 * Log
 */
const Log = {
	/**
	 * @member {boolean}
	 */
	processing: false,

	/**
	 * @member {string}
	 */
	main_page: '.snapshot-page-main',

	/**
	 * @member {string}
	 */
	logs_list: '.logs-list',

	/**
	 * Checks if logs are loaded or not.
	 *
	 * @return {boolean}
	 */
	isListReady() {
		const mainPage = document.querySelector( this.main_page );
		return ( '1' === mainPage.querySelector( this.logs_list ).getAttribute( 'data-logs-loaded' ) ) ? true : false;
	},

	/**
	 * Loads all log file.
	 *
	 * @param {HTMLButtonElement} el
	 * @return {Promise}
	 */
	all( el ) {
		if ( this.processing ) {
			return;
		}
		this.processing = true;
		const resp = Helper.deferred();

		const $mainPage = document.querySelector( this.main_page );
		const $logLists = $mainPage.querySelector( this.logs_list );
		// Prepare data for AJAX request.
		const data = {
			action: 'snapshot-get_log_list',
			_wpnonce: el.getAttribute( 'data-nonce' ),
			method: 'GET'
		};

		const request = Helper.request( data );
		request
			.then( ( response ) => response.json() )
			.then( ( result ) => {
				if ( result.success ) {
					$logLists.setAttribute( 'data-logs-loaded', 1 );
					if ( '' !== result.data.content ) {
						$logLists.querySelector( '.log-rows' ).innerHTML = result.data.content;
						$logLists.querySelector( '.logs-empty' ).style.display = 'none';
						$logLists.querySelector( '.logs-loading' ).style.display = 'none';
						$logLists.querySelector( '.logs-not-empty' ).style.display = 'block';
						resp.resolve();
					} else {
						UI.displayNotice( {
							type: 'warning',
							message: snapshot_messages.no_logs_found,
							autoclose: 3000
						} );
						resp.reject();
					}
				} else {
					UI.displayNotice( {
						type: 'warning',
						message: snapshot_messages.failed_listing_logs,
						autoclose: 3000
					} );
					resp.reject( result.data );
				}
				el.removeAttribute( 'disabled' );
				this.processing = false;
			} )
			.catch( ( err ) => {
				this.processing = false;
				el.removeAttribute( 'disabled' );
				UI.displayNotice( {
					type: 'warning',
					message: snapshot_messages.failed_listing_logs,
					autoclose: 3000
				} );
				resp.reject();
			} );

		return resp;
	},

	/**
	 * Loads the single log file.
	 *
	 * @param {*} el
	 *
	 * @return {void}
	 */
	single( el ) {
		if ( this.processing ) {
			return;
		}

		const backupId = el.getAttribute( 'data-backup-id' );
		if ( Helper.isEmpty( backupId ) ) {
			return;
		}
		this.processing = true;
		const $mainPage = document.querySelector( this.main_page );
		let $tableRow = null;
		if ( 'nodeName' in el && '' !== el.nodeName && 'tr' === el.nodeName.toLowerCase() ) {
			$tableRow = el;
		} else {
			$tableRow = $mainPage.querySelector( `tr[data-backup-id="${ backupId }"]` );
		}

		if ( ! $tableRow ) {
			this.processing = false;
			el.removeAttribute( 'disabled' );
			UI.displayNotice( {
				type: 'warning',
				message: snapshot_messages.backup_log_not_found,
				autoclose: 3000
			} );
			return;
		}
		const trSibling = $tableRow.nextElementSibling;

		const data = {
			action: 'snapshot-get_backup_log',
			_wpnonce: el.getAttribute( 'data-nonce' ),
			backup_id: backupId,
			method: 'GET'
		};

		const backup = Helper.request( data );
		backup
			.then( ( response ) => response.json() )
			.then( ( result ) => {
				trSibling.querySelector( '.snapshot-loading' ).style.display = 'none';
				trSibling.querySelector( '.snapshot-loaded' ).style.display = 'block';
				if ( result.success ) {
					$tableRow.setAttribute( 'data-append-log', 1 );

					// Hide all errors.
					UI.hideErrors( '.notice-error' );
					const $select = trSibling.querySelector( 'select' );
					if ( $select ) {
						UI.initSelect2( $select );
					}
					if ( 'items' in result.data.log ) {
						const items = result.data.log.items;

						items.forEach( ( item ) => {
							const entry = `<div class="log-item log-level-${ item.level }">
								<div class="log-item__icon" aria-hidden="true"></div>
								<div class="log-item__content">${ item.message }</div>
							</div>`;
							trSibling.querySelector( '.log-lists' ).innerHTML += entry;
						} );
					}

					// We need to set the pagination as well.
					if ( 'pages' in result.data.log && result.data.log.pages ) {
						const $paginateLog = trSibling.querySelector( '.paginate-log' );
						$paginateLog.parentNode.style.display = 'block';
						$paginateLog.setAttribute( 'data-pages', result.data.log.pages );
						$paginateLog.setAttribute( 'data-next', result.data.log.next_page );
						$paginateLog.setAttribute( 'data-current', result.data.log.current_page );
					} else if ( ! result.data.log.pages ) {
						trSibling.querySelector( '.paginate-log' ).style.display = 'none';
					}
				} else {
					let $noContent = null;
					if ( 'no_content' === result.data.status ) {
						$noContent = $mainPage.querySelector( '.no-log-content' );
					} else {
						$noContent = $mainPage.querySelector( '.general-error' );
					}

					$noContent.style.display = 'block';
				}
				el.removeAttribute( 'disabled' );
				this.processing = false;
			} )
			.catch( ( err ) => {
				// Display the error message.
				el.removeAttribute( 'disabled' );
				this.processing = false;
			} );
	},

	/**
	 * Paginates the single log file.
	 *
	 * @param {HTMLButtonElement} el
	 *
	 * @return {void}
	 */
	paginate( el ) {
		if ( this.processing ) {
			return;
		}

		const page = parseInt( el.getAttribute( 'data-current' ) );
		const next = parseInt( el.getAttribute( 'data-next' ) );
		const pages = parseInt( el.getAttribute( 'data-pages' ) );
		const backupId = el.getAttribute( 'data-backup-id' );
		const boxEl = el.closest( '.sui-box-body' );

		// We cannot proceed if we don't have "backup id".
		if ( Helper.isEmpty( backupId ) ) {
			return;
		}
		// Do we need to proceed?
		if ( page > pages || next <= 0 || page === pages ) {
			el.parentNode.style.display = 'none';
			return;
		}

		this.processing = true;
		el.setAttribute( 'disabled', 'disabled' );
		el.classList.add( 'sui-button-onload' );

		const data = {
			method: 'GET',
			action: 'snapshot-paginate_log',
			_wpnonce: el.getAttribute( 'data-nonce' ),
			offset: next,
			backup_id: backupId
		};

		const request = Helper.request( data );
		request
			.then( ( response ) => response.json() )
			.then( ( result ) => {
				if ( result.success ) {
					if ( 'items' in result.data.log ) {
						const items = result.data.log.items;

						items.forEach( ( item ) => {
							const entry = `<div class="log-item log-level-${ item.level }">
								<div class="log-item__icon" aria-hidden="true"></div>
								<div class="log-item__content">${ item.message }</div>
							</div>`;
							boxEl.querySelector( '.log-lists' ).innerHTML += entry;
						} );
					}

					if ( 'pages' in result.data.log && result.data.log.pages ) {
						el.setAttribute( 'data-current', result.data.log.current_page );
						el.setAttribute( 'data-next', result.data.log.next_page );

						if ( parseInt( result.data.log.current_page ) === parseInt( result.data.log.pages ) ) {
							el.parentNode.style.display = 'none';
						}
					} else {
						el.parentNode.style.display = 'none';
					}
				} else {
					UI.displayNotice( {
						type: 'warning',
						message: snapshot_messages.failed_listing_logs,
						autoclose: 3000
					} );
				}
				this.processing = false;
				el.removeAttribute( 'disabled' );
				el.classList.remove( 'sui-button-onload' );
			} )
			.catch( ( err ) => {
				UI.displayNotice( {
					type: 'warning',
					message: snapshot_messages.failed_listing_logs,
					autoclose: 3000
				} );
				this.processing = false;
				el.removeAttribute( 'disabled' );
				el.classList.remove( 'sui-button-onload' );
			} );
	},

	/**
	 * Filters the log entries.
	 *
	 * @param {HTMLSelectElement} e
	 *
	 * @return {void}
	 */
	filter( e ) {
		if ( '' === e.target.value ) {
			return;
		}

		const el = e.target;
		const value = el.value;
		const allErrors = el.closest( 'td' ).querySelectorAll( '.notice-error' );
		const allEntries = el.closest( 'td' ).querySelectorAll( '.log-item' );
		const error = el.closest( 'td' ).querySelector( `.no-${ value }` );
		const hasErrors = [];

		allEntries.forEach( ( entry ) => {
			if ( 'all' === value ) {
				allErrors.forEach( ( err ) => {
					err.style.display = 'none';
				} );
				entry.style.display = 'block';
			} else {
				const sel = `log-level-${ value }`;
				allErrors.forEach( ( err ) => {
					err.style.display = 'none';
				} );
				if ( entry.classList.contains( sel ) ) {
					entry.style.display = 'block';
					hasErrors.push( value );
				} else {
					entry.style.display = 'none';
				}
			}
		} );

		if ( hasErrors.length === 0 ) {
			allErrors.forEach( ( err ) => {
				err.style.display = 'none';
			} );
			if ( 'all' !== value ) {
				error.style.display = 'block';
			}
		}
	}
};

/**
 * Backup
 */
const Backup = {

	/**
	 * Initialize and register "click" listener.
	 *
	 * @return {void}
	 */
	init() {
		const $mainPage = document.querySelector( '.snapshot-page-main' );
		const _self = this;
		if ( $mainPage ) {
			$mainPage.addEventListener( 'click', ( e ) => {
				_self.click( e );
			} );
		}
	},

	/**
	 * Delegated click event.
	 *
	 * @param {Event} e
	 *
	 * @return {void}
	 */
	click( e ) {
		let el = e.target;

		if ( el.classList.contains( 'view-log' ) || el.parentNode.classList.contains( 'view-log' ) ) {
			e.preventDefault();
			if ( el.parentNode.classList.contains( 'view-log' ) ) {
				el = el.parentNode;
			}

			const mainPage = document.querySelector( Log.main_page );
			const backup = el.getAttribute( 'data-backup-id' );
			el.setAttribute( 'disabled', 'disabled' );

			if ( Log.isListReady() ) {
				const $tableRow = mainPage.querySelector( `tr[data-backup-id="${ backup }"]` );
				if ( ! $tableRow ) {
					Log.processing = false;
					el.removeAttribute( 'disabled' );
					UI.displayNotice( {
						type: 'warning',
						message: snapshot_messages.backup_log_not_found,
						autoclose: 3000
					} );
					return;
				}
				$tableRow.click();
				// Switch to "logs" tab.
				UI.switchTab( 'logs' );
				Log.single( el );
			} else {
				const lists = Log.all( el );
				lists.then( ( result ) => {
					const $tableRow = mainPage.querySelector( `tr[data-backup-id="${ backup }"]` );
					if ( ! $tableRow ) {
						Log.processing = false;
						UI.displayNotice( {
							type: 'warning',
							message: snapshot_messages.backup_log_not_found,
							autoclose: 3000
						} );
						return;
					}
					$tableRow.click();
					UI.switchTab( 'logs' );
					Log.single( el );
					const logLists = mainPage.querySelectorAll( '.log-row' );
					if ( logLists.length ) {
						logLists.forEach( ( list ) => {
							list.onclick = UI.tr_click.bind( this );
						} );
					}
					Helper.trigger( 'click', logLists );
				} )
					.catch( ( err ) => {
						Log.processing = false;
						el.removeAttribute( 'disabled' );
						UI.displayNotice( {
							type: 'warning',
							message: snapshot_messages.backup_log_not_found,
							autoclose: 3000
						} );
					} );
			}
		}

		if ( el.classList.contains( 'paginate-log' ) || el.parentNode.classList.contains( 'paginate-log' ) ) {
			e.preventDefault();
			if ( el.parentNode.classList.contains( 'paginate-log' ) ) {
				el = el.parentNode;
			}

			const loadedBox = el.closest( '.snapshot-loaded' );
			if ( loadedBox ) {
				const filterLog = loadedBox.querySelector( '.log-filter' );
				if ( 'all' !== filterLog.value ) {
					const all = filterLog.querySelector( 'option[value="all"]' );
					all.selected = true;
					filterLog.dispatchEvent( new Event( 'change' ) );
				}
			}

			/**
			 * Paginate the log file.
			 */
			Log.paginate( el );
		}
	},

	/**
	 * Reload all backups.
	 *
	 * @param {string} nonce
	 */
	reload( nonce ) {
		const data = {
			_wpnonce: nonce,
			action: 'snapshot-list_backups',
			force_refresh: '1',
			method: 'POST'
		};
		const $main_page = document.querySelector( Log.main_page );
		if ( $main_page ) {
			$main_page.querySelector( '.snapshot-listed-backups' ).style.display = 'none';
			$main_page.querySelector( '.snapshot-backup-list-loader' ).style.display = 'block';
		}

		const request = Helper.request( data );
		request
			.then( ( response ) => response.json() )
			.then( ( result ) => {
				$main_page.querySelector( '.snapshot-backup-list-loader' ).style.display = 'none';
				$main_page.querySelector( '.snapshot-listed-backups' ).style.display = 'block';
				UI.build_backups_table( result.data );
			} )
			.catch( ( err ) => {
				console.log( err );
			} );
	},
};
// Initialize the backup logs.
Backup.init();

// Notified flag.
let notified = false;

// Backup Export.
const Export = {
	init() {
		const _self = this;
		const wpnonceElement = document.querySelector( '#_wpnonce-export-backup' );
		const request_data = {
			action: 'snapshot-export_backup',
			_wpnonce: wpnonceElement?.value || '',
			send_email: 0,
		};

		document.addEventListener( 'click', ( e ) => {
			const el = e.target;

			if ( el.classList.contains( 'snapshot--send__email' ) || el.parentNode.classList.contains( 'snapshot--send__email' ) ) {
				e.preventDefault();
				request_data.send_email = 1;
				_self.send_email( e, request_data );
			}

			if ( el.classList.contains( 'snapshot--direct__download' ) || el.parentNode.classList.contains( 'snapshot--direct__download' ) ) {
				e.preventDefault();
				_self.direct_download( e, request_data );
			}
		} );

		const export_form = document.querySelector( '#snapshot-export-form' );
		if ( export_form ) {
			export_form.addEventListener( 'submit', ( event ) => {
				// Prevent the default form submission event for export form.
				event.preventDefault();
			} );
		}
	},

	snapshotExportEvent( { snapshot_id, export_id, export_status, export_type, export_process } ) {
		return new CustomEvent( 'snapshot-export', {
			detail: {
				snapshot_id,
				export_id,
				export_status,
				export_type,
				export_process,
			},
			cancelable: true // Make the event cancelable
		} );
	},

	send_email( evt, data ) {
		const el = evt.target;
		const form = el.closest( '#snapshot-export-form' );

		// Append backup_id to the form data.
		data.backup_id = form.querySelector( '#snapshot--backup__id' ).value;

		const sendToEmailRadios = form.querySelectorAll( 'input[name="send-to-mail"]' );

		let sendToEmailValue = '';
		for ( const radio of sendToEmailRadios ) {
			if ( radio.checked ) {
				sendToEmailValue = radio.value;
				break;
			}
		}

		if ( 'different-mail' === sendToEmailValue ) {
			const emailField = form.querySelector( '[name="email-address"]' );
			const email = emailField.value;

			if ( ! /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test( email ) ) {
				emailField.focus();
				emailField.closest( '.sui-form-field' ).classList.add( 'sui-form-field-error' );
				form.querySelector( '#email-address--error_message' ).style.display = 'block';
				return;
			}

			emailField.closest( '.sui-form-field' ).classList.remove( 'sui-form-field-error' );
			form.querySelector( '#email-address--error_message' ).style.display = 'none';
			data.email_address = email;
		}

		const export_types = form.querySelectorAll( 'input[name="export_options"]' );
		export_types.forEach( ( export_type ) => {
			if ( export_type.checked ) {
				data.export_type = export_type.value;
			}
		} );

		el.setAttribute( 'disabled', 'disabled' );

		const request = Helper.request( data );

		request
			.then( ( response ) => response.json() )
			.then( ( result ) => {
				if ( result.success ) {
					const noticeObj = {
						type: 'info',
						dismissible: true,
					};

					if ( 'api_response' in result.data && 'message' in result.data.api_response ) {
						const message = result.data.api_response.message;

						if ( 'Export already in progress, please try after some time' === message ) {
							document.dispatchEvent( snapshotExport );
							noticeObj.message = snapshot_messages.backup_export_already_requested.replace( '%s', name );
						}

						if ( message.includes( 'Export already exist' ) ) {
							noticeObj.message = snapshot_messages.backup_export_success.replace( '%s', name );
						}
					} else {
						noticeObj.message = snapshot_messages.backup_export_success.replace( '%s', result.data.site );
						const snapshotExport = this.snapshotExportEvent( {
							snapshot_id: data.backup_id,
							export_id: result.data.api_response.export_id,
							export_status: result.data.api_response.export_status,
							export_type: data.export_type,
							export_process: 'send_email',
						} );
						document.dispatchEvent( snapshotExport );
					}

					UI.displayNotice( noticeObj );
				} else {
					UI.displayNotice( {
						type: 'error',
						message: snapshot_messages.backup_export_error,
					} );
				}
				el.removeAttribute( 'disabled' );
				SUI.closeModal();
			} );
	},

	direct_download( evt, data ) {
		const el = evt.target;
		const form = el.closest( '#snapshot-export-form' );
		data.backup_id = form.querySelector( '#snapshot--backup__id' ).value;
		data.direct_download = '1';

		const export_types = form.querySelectorAll( 'input[name="export_options"]' );
		export_types.forEach( ( export_type ) => {
			if ( export_type.checked ) {
				data.export_type = export_type.value;
			}
		} );

		el.setAttribute( 'disabled', 'disabled' );
		const request = Helper.request( data );

		request
			.then( ( response ) => response.json() )
			.then( ( result ) => {
				if ( result.success ) {
					const noticeObj = {
						type: 'info',
						dismissible: true,
					};

					if ( 'api_response' in result.data && 'message' in result.data.api_response ) {
						const message = result.data.api_response.message;

						if ( 'Export already in progress, please try after some time' === message ) {
							noticeObj.message = snapshot_messages.backup_export_already_requested.replace( '%s', name );
						}

						if ( message.includes( 'Export already exist' ) ) {
							noticeObj.message = snapshot_messages.backup_export_success.replace( '%s', name );
						}
					} else {
						if ( notified ) {
							notified = false;
						}
						noticeObj.message = snapshot_messages.backup_export_success_download.replace( '%s', result.data.site );
						const snapshotExport = this.snapshotExportEvent( {
							snapshot_id: data.backup_id,
							export_id: result.data.api_response.export_id,
							export_status: result.data.api_response.export_status,
							export_type: data.export_type,
							export_process: 'direct_download',
						} );
						document.dispatchEvent( snapshotExport );
					}

					if ( 'message' in noticeObj ) {
						UI.displayNotice( noticeObj );
					}
				} else {
					UI.displayNotice( {
						type: 'error',
						message: snapshot_messages.backup_export_error,
					} );
				}
				el.removeAttribute( 'disabled' );
				SUI.closeModal();
			} );
	}
};
// Export.init();\
document.addEventListener( 'DOMContentLoaded', () => {
	Export.init();
	ExportUI.init();
} );

const build_methods = document.querySelectorAll( '.snapshot-settings--db__method .sui-tab-item' );
if ( build_methods ) {
	const notice = document.querySelector( '.snapshot-settings--db__method .sui-notice--mysqldump' );
	build_methods.forEach( ( method ) => {
		method.addEventListener( 'click', () => {
			if ( ! notice ) {
				return;
			}

			const value = method.querySelector( 'input' ).value;
			const save = document.querySelector( '.snapshot-settings--save__button' );

			if ( 'php_code' === value ) {
				save.removeAttribute( 'disabled' );
				notice.style.display = 'none';
			} else {
				save.setAttribute( 'disabled', 'disabled' );
				notice.style.display = 'block';
			}
		} );
	} );
}

const $main_page = document.querySelector( '.snapshot-page-main' );
const submit_backup_comment_form = ( e ) => {
	if ( e && e.preventDefault() ) {
		e.preventDefault();
	}

	const el = e ? e.target : null;
	if ( null === el ) {
		return;
	}

	const backupId = el.querySelector( '#comment-backup-id' ).value;
	let comment = el.querySelector( 'textarea' ).value;

	if ( null === backupId || 'undefined' === backupId || '' === backupId ) {
		el.closest( '.sui-box-body' ).querySelector( '#notice-no-backup-id' ).style.display = 'block';
		return;
	}

	const btn = el.querySelector( 'button[type=submit]' );
	const action = btn.getAttribute( 'data-action-type' ); // add|edit actions.

	el.closest( '.sui-box-body' ).querySelector( '#notice-no-backup-id' ).style.display = 'none';

	comment = comment.trim();
	if ( '' === comment || null === comment || 'undefined' === comment ) {
		comment = '';
	}

	// Show loading icon on button.
	btn.classList.add( 'sui-button-onload' );

	const formData = new FormData();
	formData.append( 'comment_type', action );
	formData.append( 'description', comment );
	formData.append( 'backup_id', backupId );
	formData.append( 'action', 'snapshot-update_backup' );
	formData.append( '_wpnonce', el.querySelector( '#_wpnonce-snapshot_update_backup_comment' ).value );

	const request = fetch( SnapshotAjax.ajaxurl, {
		method: 'POST',
		credentials: 'same-origin',
		body: formData
	} );
	request
		.then( ( response ) => response.json() )
		.then( ( result ) => {
			Backup.reload( result.data.nonce );

			if ( result.success ) {
				btn.classList.remove( 'sui-button-onload' );
				SUI.closeModal();
				let msg = null;
				if ( 'edit' === action ) {
					msg = snapshot_messages.comment_updated;
				} else if ( 'add' === action ) {
					msg = snapshot_messages.comment_added;
				}
				UI.displayNotice( {
					type: 'success',
					message: msg,
					dismissible: true
				} );
			} else {
			}
		} )
		.catch( ( err ) => {
			btn.classList.remove( 'sui-button-onload' );
			SUI.closeModal();
			UI.displayNotice( {
				type: 'warning',
				message: snapshot_messages.generic_error,
				autoclose: 3000
			} );
		} );
};

const $backupForm = document.querySelector( '#form-snapshot-edit-manual-backup-comment' );
if ( $backupForm ) {
	// Add backup form 'submit' event listener.
	$backupForm.addEventListener( 'submit', ( e ) => {
		submit_backup_comment_form( e );
	} );
}

/**
 * Binds the click event.
 *
 * @param {Event} e
 */
const open_backup_comment_modal = ( e ) => {
	let el = e.target;

	if (
		el.classList.contains( 'open-edit-backup' ) ||
		el.parentNode.classList.contains( 'open-edit-backup' )
	) {
		e.preventDefault();

		if ( el.classList.contains( 'sui-icon-pencil' ) || el.classList.contains( 'snapshot-backup--content' ) ) {
			el = el.parentNode;
		}

		const type = el.getAttribute( 'data-type' );
		const modal = 'modal-snapshot-update-backup-comment';
		const button = document.querySelector( `#${ modal }` ).querySelector( 'button[type=submit]' );
		const comment = el.getAttribute( 'data-tooltip' );
		const snapshot = el.closest( 'tr' ).getAttribute( 'data-backup_id' );

		if ( 'edit' === type ) {
			if ( comment ) {
				document.querySelector( `#${ modal }` ).querySelector( '#manual-backup-comment-modal' ).value = comment.trim();
			}
			button.querySelector( '.sui-button-text-default' ).innerHTML = snapshot_messages.edit_comment_text;
		} else if ( 'add' === type ) {
			button.querySelector( '.sui-button-text-default' ).innerHTML = snapshot_messages.add_comment_text;
			document.querySelector( `#${ modal }` ).querySelector( '#manual-backup-comment-modal' ).value = '';
		}
		document.querySelector( `#${ modal }` ).querySelector( '#comment-backup-id' ).value = snapshot;
		button.setAttribute( 'data-action-type', type );
		if ( button.classList.contains( 'sui-button-onload' ) ) {
			button.classList.remove( 'sui-button-onload' );
		}

		SUI.openModal( modal, el );
	}
};

let $textArea = null;
if ( $main_page ) {
	// Attach the 'click' event listener to the main page.
	$main_page.addEventListener( 'click', open_backup_comment_modal );
	$textArea = $main_page.querySelector( '#snapshot-file-exclusions' );
}

/**
 * Dispatches an event.
 *
 * @param {*}      $el   Element where the events need to be dispatched.
 * @param {string} event Event name.
 */
const trigger = ( $el, event ) => {
	$el.dispatchEvent( new Event( event ) );
};

/**
 * Adds list of files to exclusion.
 *
 * @param {Event} e
 */
const handle_exclusion_filter = ( e ) => {
	const el = e.target;

	if ( el.classList.contains( 'snapshot-filter-action' ) ) {
		e.preventDefault();

		const $multiStringsTextArea = $main_page.querySelector( '#snapshot-file-exclusions' );
		let textAreaValue = $multiStringsTextArea.value;

		if ( el.classList.contains( 'snapshot-filter-action--wp__core' ) ) {
			// Exclude all WordPress core files.
			textAreaValue += ' \n';
			textAreaValue += [
				'/wp-admin/',
				'/wp-includes/',
				'/index.php',
				'/license.txt',
				'/readme.html',
				'/wp-activate.php',
				'/wp-blog-header.php',
				'/wp-comments-post.php',
				'/wp-config-sample.php',
				'/wp-config.php',
				'/wp-cron.php',
				'/wp-links-opml.php',
				'/wp-load.php',
				'/wp-login.php',
				'/wp-mail.php',
				'/wp-settings.php',
				'/wp-signup.php',
				'/wp-trackback.php',
				'/xmlrpc.php',
			].join( '\n' );
			textAreaValue += '\n';
		}

		if ( el.classList.contains( 'snapshot-filter-action--wp__themes' ) ) {
			textAreaValue += '\n /wp-content/themes/ \n';
		}

		if ( el.classList.contains( 'snapshot-filter-action--wp__plugins' ) ) {
			textAreaValue += '\n /wp-content/plugins/ \n';
		}

		$multiStringsTextArea.value = textAreaValue;
		trigger( $multiStringsTextArea, 'change' );
	}
};

if ( $main_page ) {
	$main_page.addEventListener( 'click', ( e ) => {
		handle_exclusion_filter( e );
	} );

	/**
	 * Display the "Clear exclusions" when "enter" is pressed.
	 */
	window.addEventListener( 'keyup', ( e ) => {
		if ( e.key && 'enter' === e.key.toLowerCase() ) {
			const $lists = $main_page.querySelector( '.sui-multistrings-list' );

			if ( $lists ) {
				const $allLists = $lists.querySelectorAll( 'li' );
				if ( $allLists.length > 2 ) {
					e.preventDefault();
					trigger( $textArea, 'change' );
				}
			}
		}
	} );
}

( function( $ ) {
	// Get all radio inputs.
	const radioInputs = document.querySelectorAll( 'input[type="radio"][name="send-to-mail"]' );
	if ( radioInputs ) {
		// Add event listener to each radio input.
		radioInputs.forEach( ( radio ) => {
			radio.addEventListener( 'change', () => {
				// Get the element ID from the aria-controls attribute.
				const controlledElement = radio.getAttribute( 'aria-controls' );

				// Remove the 'active' class from all controlled elements.
				radioInputs.forEach( ( input ) => {
					const elementToToggle = document.querySelector( '.' + input.getAttribute( 'aria-controls' ) );
					if ( elementToToggle ) {
						elementToToggle.classList.remove( 'active' );
						elementToToggle.style.display = 'none';
					}
				} );

				// Add the 'active' class to the currently controlled element.
				const currentControlledElement = document.querySelector( '.' + controlledElement );
				if ( currentControlledElement ) {
					currentControlledElement.classList.add( 'active' );
					currentControlledElement.style.display = 'block';
				}
			} );
		} );
	}

	// Offset for continuous update of the running backup log, false - if don't need to update the log.
	let update_log_offset = false;

	// Allow auto scrolling in log modal.
	let log_allow_auto_scroll = true;

	// Previous scroll position in log modal.
	let log_prev_scroll = 0;

	let last_schedule_info = null;

	/**
	 * Tabs in Backups page. Array - [box selector, vertical selector, mobile nav selector].
	 *
	 * @type {Object}
	 */
	const navbar_tabs = {
		backups: [ '.snapshot-list-backups', '.snapshot-vertical-backups' ],
		logs: [ '.snapshot-logs', '.snapshot-vertical-logs' ],
		settings: [ '.snapshot-backups-settings', '.snapshot-vertical-settings' ],
		notifications: [ '.snapshot-notifications', '.snapshot-vertical-notifications' ]
	};

	/**
	 * Toggles the navbar to show specified tab.
	 *
	 * @param {string} tab Tab in Backups page.
	 */
	function toggle_navbar( tab ) {
		for ( const current_tab in navbar_tabs ) {
			const box_selector = navbar_tabs[ current_tab ][ 0 ];
			const vertical_selector = navbar_tabs[ current_tab ][ 1 ];

			if ( tab === current_tab ) {
				// Toggle to a box.
				$( '.snapshot-page-main' ).find( box_selector ).show();
				// Make a sidenav active.
				$( '.snapshot-page-main' ).find( vertical_selector ).addClass( 'current' );
			} else {
				$( '.snapshot-page-main' ).find( box_selector ).hide();
				$( '.snapshot-page-main' ).find( vertical_selector ).removeClass( 'current' );
			}
		}

		if ( tab === 'logs' ) {
			reload_logs( false );
		}

		return false;
	}

	function get_current_storage() {
		const url = ajaxurl + '?action=snapshot-get_storage';
		const data = {
			_wpnonce: $( '#_wpnonce-snapshot_get_storage' ).val()
		};

		$.ajax( {
			type: 'POST',
			url,
			data,
			cache: false,
			dataType: 'json',
			success( response ) {
				if ( response.success ) {
					const widget = $( '.snapshot-storage-widget' );
					if ( response.data.width_float >= 0.95 ) {
						widget.addClass( 'full' );
						if ( ! $( '.insufficient-storage-space-notice' ).length ) {
							const notice = $( '<span class="insufficient-storage-space-notice"></span>' )
								.html( snapshot_messages.insufficient_storage_space_notice );
							jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', notice ] );
						}
					} else {
						widget.removeClass( 'full' );
						$( '.insufficient-storage-space-notice' ).trigger( 'snapshot:close_notice' );
					}
					widget.find( '.snapshot-storage-used-value' ).html( response.data.current_stats.replace( /\//, ' /' ) );
					widget.find( '.sui-progress-bar > span' ).css( { width: response.data.width } );
					widget.css( 'visibility', 'visible' );
				}
			}
		} );
	}

	// Retrieve backups info list.
	function snapshot_list_backups( force_refresh ) {
		if ( force_refresh === undefined ) {
			force_refresh = false;
		}

		get_current_storage();

		const data = {
			_wpnonce: $( '#_wpnonce-list-backups' ).val(),
			force_refresh: force_refresh ? 1 : 0
		};

		$( '.snapshot-listed-backups' ).hide();
		$( '.snapshot-no-backups' ).hide();
		// $('.wps-backup-list-ajax-error').hide();

		const deferred = jQuery.Deferred();

		const list_backups_href = ajaxurl + '?action=snapshot-list_backups';

		jQuery.ajax( {
			type: 'POST',
			url: list_backups_href,
			data,
			cache: false,
			dataType: 'json',
			beforeSend() {
				$( '.sui-box-header.snapshot-has-backups-title' ).hide();
				$( '.snapshot-loading' ).show();
				$( '.snapshot-list-backups .api-error' ).hide();
				$( '.sui-summary-details.snapshot-backups-number .sui-summary-large' ).text( '' );
				$( '.sui-summary-segment .snapshot-last-backup' ).text( '' );
			},
			complete() {
				$( '.snapshot-loading' ).hide();
			},
			success( reply_data ) {
				if ( reply_data.success && reply_data.data.backups !== undefined ) {
					window.backups_number = reply_data.data.backups.length;
					window.failed_backups_number = reply_data.data.failed_backups || 0;

					$( '.snapshot-listed-backups .sui-table > tbody' ).empty();

					// Deal with updating the number of backups.
					$( '.sui-summary-details.snapshot-backups-number .sui-summary-large' ).html( backups_number - failed_backups_number );

					if ( reply_data.data.backups && backups_number !== 0 ) {
						// Deal with the Last Backup section.
						window.lastBackup = reply_data.data.backups[ 0 ];
						$( '.sui-summary-segment .snapshot-last-backup' ).html( lastBackup.date );

						// Disable the Backup Now button, if we're still in the same minute with the last backup taken.
						const time_elapsed = ( Date.now() / 1000 - lastBackup.timestamp ) / 60;
						$( window ).trigger( 'snapshot:toggle_cooldown', [ time_elapsed ] );

						// Deal with the backup listing and loader.
						$( '.snapshot-listed-backups' ).show();
						$( '.snapshot-no-backups' ).hide();
						$( '.sui-box-header.snapshot-has-backups-title' ).show();

						$.each( reply_data.data.backups, function( i, item ) {
							const row = $( item.row );
							if ( ! row.hasClass( 'snapshot-failed-backup' ) ) {
								row.addClass( 'sui-accordion-item' );
							}
							$( '.snapshot-listed-backups .sui-table > tbody:last-child' ).append( row );
							$( '.snapshot-listed-backups .sui-table > tbody:last-child' ).append( item.row_content );
						} );
						update_backup_rows_schedule();

						fix_export_destination_tooltips();
					} else {
						// Deal with the Last Backup section.
						$( '.sui-summary-segment .snapshot-last-backup' ).text( snapshot_messages.last_backup_unknown_date );

						if ( false === reply_data.data.backup_running ) {
							// Deal with the backup listing header.
							$( '.sui-box-header.snapshot-has-backups-title' ).hide();

							// Deal with the backup listing and loader.
							$( '.snapshot-no-backups' ).show();
						} else {
							$( '.snapshot-listed-backups' ).show();
							$( '.sui-box-header.snapshot-has-backups-title' ).show();
						}
					}

					deferred.resolve( reply_data );
				} else {
					deferred.reject( 'error' );
				}
				if ( ! reply_data.success ) {
					show_api_error();
				}
			},
			error() {
				deferred.reject( 'HTTP error' );
				show_api_error();
			}
		} );

		return deferred.promise();
	}

	function fix_export_destination_tooltips() {
		// Workaround to both show the size tooltip above the icon and to show it when hovering the whole span.
		$( '.snapshot-tooltip-size' ).on( 'mouseenter', function() {
			$( this ).parent().find( '.snapshot-icon-tooltip2' ).show();
			$( this ).parent().find( '.snapshot-icon-tooltip' ).hide();
		} ).on( 'mouseleave', function() {
			$( this ).parent().find( '.snapshot-icon-tooltip' ).show();
			$( this ).parent().find( '.snapshot-icon-tooltip2' ).hide();
		} );

		// Workaround to both show the export tooltip above the icon and to show it when hovering the whole span.
		$( '.snapshot-export-details-failure' ).parent().find( '.snapshot-export-backup-details' ).on( 'mouseenter', function() {
			$( this ).parent().find( '.snapshot-export-details-failure2' ).show();
			$( this ).parent().find( '.snapshot-export-details-failure' ).hide();
		} ).on( 'mouseleave', function() {
			$( this ).parent().find( '.snapshot-export-details-failure' ).show();
			$( this ).parent().find( '.snapshot-export-details-failure2' ).hide();
		} );

		// Workaround to hide the destination list tooltip when the i tooltip is hovered.
		$( window ).trigger( 'snapshot:hide_double_tooltip' );
	}

	function show_api_error() {
		$( '.snapshot-list-backups .api-error' ).show();
		$( '.sui-summary-details.snapshot-backups-number .sui-summary-large' ).text( '-' );
		$( '.sui-summary-segment .snapshot-last-backup' ).text( '-' );
		$( '.sui-box-header.snapshot-has-backups-title' ).show();
	}

	function snapshot_get_schedule( open_modal, data ) {
		const schedule_modal = function( data ) {
			$( '#snapshot-backup-schedule' ).find( '>span' ).text( data.text );
			$( '#snapshot-backup-schedule' ).data( 'values', data.values );
			$( '.snapshot-backups-summary .snapshot-next-backup' ).text( data.next_backup_time );
			if ( open_modal ) {
				$( window ).trigger( 'snapshot:schedule', [ data.schedule_action, data.values, data.schedule_action ] );
			}
		};

		const url = ajaxurl + '?action=snapshot-get_schedule';
		const request_data = {
			_wpnonce: $( '#_wpnonce-get-schedule' ).val()
		};

		if ( data ) {
			schedule_modal( data );
		} else {
			$.ajax( {
				type: 'GET',
				url,
				data: request_data,
				cache: false,
				dataType: 'json',
				beforeSend() {
					last_schedule_info = null;
					$( '.sui-summary-segment .snapshot-next-backup' ).text( '' );
					$( '.sui-summary-segment .snapshot-schedule-frequency' ).text( '' );
					$( '#snapshot-backup-schedule .button-manage' ).hide();
					$( '.snapshot-loading-schedule' ).show();
				},
				success( response ) {
					if ( response.success ) {
						schedule_modal( response.data );
						$( '#snapshot-backup-schedule' ).data( 'modalData', response.data );

						$( '.sui-summary-segment .snapshot-next-backup' ).text( response.data.next_backup_time );
						$( '.sui-summary-segment .snapshot-schedule-frequency' ).text( response.data.text );
						$( '#snapshot-backup-schedule' ).data( 'modalData', response.data );
						$( '#snapshot-backup-schedule .button-manage' ).show();
						last_schedule_info = response.data;
						update_backup_rows_schedule();
					} else {
						on_load_schedule_error();
					}
				},
				complete() {
					$( '.snapshot-loading-schedule' ).hide();
				},
				error() {
					on_load_schedule_error();
				}
			} );
		}
	}

	function update_backup_rows_schedule() {
		if ( last_schedule_info ) {
			const line = $( '.snapshot-listed-backups .open-edit-schedule' );
			line.data( 'modalData', last_schedule_info );
			line.find( '>span.schedule' ).text( last_schedule_info.text );
			line.show();
			$( '.snapshot-listed-backups .row-current-schedule .sui-loading' ).hide();
		}
	}

	function on_load_schedule_error() {
		jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.get_schedule_error ] );
		$( '.sui-summary-segment .snapshot-next-backup' ).text( '-' );
		$( '.sui-summary-segment .snapshot-schedule-frequency' ).text( '-' );

		$( '.snapshot-listed-backups .row-current-schedule .sui-loading' ).hide();
	}

	function snapshot_backup_progress( needs_api_call, already_running_backup_status, already_running_backup ) {
		if ( $( '.snapshot-listed-backups .sui-table > tbody:last-child .current-backup-row' ).data( 'updating' ) ) {
			return;
		}

		needs_api_call = needs_api_call || '0';
		already_running_backup_status = already_running_backup_status || '0';
		already_running_backup = already_running_backup || '0';

		const refresh_interval = 5000;

		const url = ajaxurl + '?action=snapshot-update_backup_progress';
		const request_data = {
			_wpnonce: $( '#_wpnonce-backup-progress' ).val(),
			do_api_call: needs_api_call,
			already_running_backup,
			already_running_backup_status
		};

		if ( update_log_offset !== false ) {
			// Also get new log entries since the last update.
			request_data.expand = 'log';
			request_data.log_offset = update_log_offset;
		}

		$.ajax( {
			type: 'GET',
			url,
			data: request_data,
			cache: false,
			dataType: 'json',
			success( response ) {
				const tbody = $( '.snapshot-listed-backups .sui-table > tbody:last-child' );
				const row = tbody.find( '.current-backup-row' );
				const row_details = tbody.find( '.current-backup-details' );
				if ( response.success ) {
					if ( true === response.data.backup_cancelled ) {
						backup_is_cancelled();
					} else if ( true === response.data.backup_failed ) {
						// Running backup has failed.

						if ( response.data.error_message_html ) {
							var notice = $( response.data.error_message_html );
							const log_link = notice.find( '.snapshot-log-link' );
							log_link.on( 'click', function( e ) {
								const link = this;
								$( link ).data( 'backupId', already_running_backup.id );
								e.preventDefault();
								notice.trigger( 'snapshot:close_notice' );
								// reload logs on log tab before switching to it.
								reload_logs( true ).then( function() {
									view_log.bind( link )();
									toggle_navbar( 'logs' );
									$( window ).trigger( 'snapshot:close_modal' );
								} );
							} );
							jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', notice ] );
						} else {
							var notice = $( '<span></span>' ).html( snapshot_messages.running_backup_fail );
							const a = notice.find( 'a' );
							a.eq( 0 ).on( 'click', function( e ) {
								const link = this;
								$( link ).data( 'backupId', already_running_backup.id );
								e.preventDefault();
								notice.trigger( 'snapshot:close_notice' );
								// reload logs on log tab before switching to it.
								reload_logs( true ).then( function() {
									view_log.bind( link )();
									toggle_navbar( 'logs' );
									$( window ).trigger( 'snapshot:close_modal' );
								} );
							} );
							a.eq( 1 ).on( 'click', function( e ) {
								e.preventDefault();
								notice.trigger( 'snapshot:close_notice' );

								jQuery( window ).trigger( 'snapshot:backup_modal' );
							} );

							jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', notice ] );
							const snippet = notice.find( '.sui-code-snippet' );
							if ( snippet ) {
								snippet.SUICodeSnippet( {
									copyText: '<span class="sui-icon-copy" aria-hidden="true"></span>'
								} );
							}
							snippet.closest( '.sui-code-snippet-wrapper' ).find( '.sui-button' ).removeClass( 'sui-button' ).addClass( 'sui-button-icon' );
						}

						$( '.button-create-backup' ).prop( 'disabled', false );

						reload_logs( true );

						snapshot_list_backups( true );
					} else if ( 'snapshot_completed' === response.data.backup_running_status ) {
						// Running backup is completed, yay.
						$( '#snapshot-modal-cancel-backup #snapshot-cancel-backup' ).prop( 'disabled', true );
						update_last_backup_row();
					} else if ( false !== response.data.backup_running ) {
						// Running backup is yet to be completed, keep doing what you doing.
						const data = $( response.data.backup_running_row ).data();
						if ( row_details.length && data.percent > 0 ) {
							row_details.find( '.button-view-log' ).prop( 'disabled', false );
							row_details.find( '.button-cancel-backup' ).prop( 'disabled', false );
						}
						if ( row.length ) {
							// Don't replace the entire block
							// in order to preserve animation phase of loader and prevent its jitter...
							row.find( '>tr' ).attr( 'id', data.id );
							row.data( 'backupId', data.backupId );
							row.find( '.backup-name' ).text( data.name );
							row.find( '.progress-text' ).text( data.progressText );

							// Build the Export Destination column.
							let export_destination_text;
							if ( 'None' === response.data.export_text.row ) {
								export_destination_text = snapshot_messages.no_destinations;
							} else if ( 'Loading' === response.data.export_text ) {
								export_destination_text = "<span class='sui-icon-loader sui-loading snapshot-destination-loader' aria-hidden='true'></span>" + snapshot_messages.loading_destinations;
							} else if ( typeof response.data.export_text.html !== 'undefined' ) {
								const exports = response.data.export_text.html;
								if ( 1 < exports.exports_count ) {
									const export_text = exports.first_export + snapshot_messages.more_destinations.replace( '%d', exports.exports_count - 1 );
									export_destination_text = "<span class='snapshot-export-backup-header snapshot-" + exports.first_export_type + "-export-backup-header sui-tooltip sui-tooltip-left-mobile sui-tooltip-constrained' style='--tooltip-width: 170px;' data-tooltip='" + exports.exports_tooltip + "'>" + export_text + '</span>';
								} else {
									export_destination_text = "<span class='snapshot-export-backup-header snapshot-" + exports.first_export_type + "-export-backup-header'>" + exports.first_export + '</span>';
								}
							}
							row.find( '.snapshot-backup-export-destinations span' ).html( export_destination_text );
							row_details.find( '.snapshot-backup-export-destinations span' ).html( export_destination_text );

							row.find( '.percent-width' ).css( 'width', data.percentWidth );
							$( '.current-backup-step-wrap' ).each( function() {
								const wrap = $( this );
								wrap.attr( 'class' ).split( ' ' ).filter( function( cl ) {
									return cl.match( /^step\-/ );
								} ).forEach( function( cl ) {
									wrap.removeClass( cl );
								} );
								const progressPercent = wrap.find( '.progressbar-container .sui-screen-reader-text > p' );
								progressPercent.text( progressPercent.text().replace( /\d+%/, data.progressText ) );
							} );
							$( '.current-backup-step-wrap' ).addClass( 'step-' + data.step );
							$( '.current-backup-step-wrap .current-step' ).text( 'Step ' + data.step + '/' + data.stepMax );
						} else {
							tbody.prepend( response.data.backup_running_row );
						}

						let next_running_backup = 0;
						// If backup still at trigger stage, repeat the progress call with data from the db, if not repeat with data from the DOM.
						if ( 'manual' !== response.data.backup_running.id ) {
							next_running_backup = response.data.backup_running;
						}

						const displayed_msgs = document.querySelectorAll( '.sui-notice.sui-active .sui-notice-message' );
						displayed_msgs.forEach( ( displayed_msg ) => {
							if ( snapshot_messages.update_progress_fail === displayed_msg.innerText && ( 'block' === displayed_msg.parentNode.parentNode.style.display ) ) {
								const notice_node = displayed_msg.parentNode.parentNode;
								notice_node.style.display = 'none';
								notice_node.classList.remove( 'sui-active' );
							}
						} );

						// Let's repeat the process, but this time make an api call to get the status.
						// We don't need to do that upon page load, because we use the info taken and stored by the Snapshot_Task_Request_Listing upon page load.
						setTimeout( snapshot_backup_progress.bind( this, '1', response.data.backup_running_status, next_running_backup ), refresh_interval );
					}
				} else if ( $( '#snapshot-modal-cancel-backup #snapshot-cancel-backup' ).hasClass( 'sui-button-onload-text' ) ) {
					backup_is_cancelled();
				} else {
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.update_progress_fail ] );
				}

				if ( response.data && response.data.log ) {
					// Update log with new entries and hide loader if snapshot_completed or backup_failed.
					const done = 'snapshot_completed' === response.data.backup_running_status || response.data.backup_failed;
					update_log( response.data.log, false, ! done );
					// Set offset for next update or stop updating if done.
					update_log_offset = ( done || update_log_offset === false ) ? false : response.data.log.size;
				} else if ( already_running_backup.id && update_log_offset !== false ) {
					// Get new log entries since the last update.
					update_log_ajax( already_running_backup.id, update_log_offset, false, false );
					// and stop continuous update of the log.
					update_log_offset = false;
				} else {
					// Hide loader in the log modal.
					update_log( null, false, false );
				}
			},
			error() {
				// Hide loader in the log modal.
				update_log( null, false, false );

				const displayed_msgs = document.querySelectorAll( '.sui-notice.sui-active .sui-notice-message' );
				let is_progress_notice_displayed = false;
				displayed_msgs.forEach( ( displayed_msg ) => {
					if ( snapshot_messages.update_progress_fail === displayed_msg.innerText && ( 'block' === displayed_msg.parentNode.parentNode.style.display ) ) {
						is_progress_notice_displayed = true;
					}
				} );

				if ( ! is_progress_notice_displayed ) {
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.update_progress_fail ] );
				}

				setTimeout( snapshot_backup_progress.bind( this, '1' ), refresh_interval );
			}
		} );
	}

	function update_last_backup_row() {
		const tbody = $( '.snapshot-listed-backups .sui-table > tbody:last-child' );
		let row = tbody.find( '.current-backup-row' );
		if ( ! row.length || row.data( 'updating' ) ) {
			return;
		}
		row.data( 'updating', true );

		reload_logs();

		$.ajax( {
			type: 'POST',
			url: ajaxurl,
			data: {
				action: 'snapshot-list_backups',
				_wpnonce: $( '#_wpnonce-list-backups' ).val(),
				force_refresh: 1
			},
			cache: false,
			dataType: 'json',
			success( data ) {
				if ( data.success && data.data.backups !== undefined ) {
					const item = data.data.backups[ 0 ];
					const backups_number = data.data.backups.length;
					failed_backups_number = data.data.failed_backups || 0;
					const last_backup = data.data.backups[ 0 ];

					// Disable the Backup Now button, if we're still in the same minute with the last backup taken.
					const time_elapsed = ( Date.now() / 1000 - last_backup.timestamp ) / 60;
					$( window ).trigger( 'snapshot:toggle_cooldown', [ time_elapsed ] );

					row.find( '.progress-text' ).text( '100%' );
					row.find( '.percent-width' ).css( 'width', '100%' );
					const step_max = row.data( 'stepMax' );
					$( '.current-backup-step-wrap' ).each( function() {
						const wrap = $( this );
						wrap.attr( 'class' ).split( ' ' ).filter( function( cl ) {
							return cl.match( /^step\-/ );
						} ).forEach( function( cl ) {
							wrap.removeClass( cl );
						} );
						const progressPercent = wrap.find( '.progressbar-container .sui-screen-reader-text > p' );
						progressPercent.text( progressPercent.text().replace( /\d+%/, '100%' ) );
					} );
					$( '.current-backup-step-wrap' ).addClass( 'step-' + step_max );
					$( '.current-backup-step-wrap .current-step' ).text( 'Step ' + step_max + '/' + step_max );

					$( '.snapshot-listed-backups .snapshot-details-row' ).removeClass( 'snapshot-last-backup' );
					setTimeout( function() {
						$( '.button-create-backup' ).prop( 'disabled', false );

						row.remove();
						row = $( item.row );
						const row_content = $( item.row_content );
						if ( ! row.hasClass( 'snapshot-failed-backup' ) ) {
						    row.addClass( 'sui-accordion-item' );
						}
						$( '.snapshot-listed-backups .sui-table > tbody:last-child' ).prepend( row_content );
						$( '.snapshot-listed-backups .sui-table > tbody:last-child' ).prepend( row );
						update_backup_rows_schedule();

						// Workaround to hide the destination list tooltip when the i tooltip is hovered.
						$( window ).trigger( 'snapshot:hide_double_tooltip' );

						$( '.sui-summary-details.snapshot-backups-number .sui-summary-large' ).html( backups_number - failed_backups_number );
						$( '.sui-summary-segment .snapshot-last-backup' ).html( last_backup.date );
						// Show the appropriate message of backup completion.
						const export_header = row.find( '.snapshot-export-backup-header' );
						let backup_complete_msg,
							backup_complete_icon;
						if ( ! export_header.length ) {
							backup_complete_msg = snapshot_messages.create_backup_success;
							backup_complete_icon = 'success';
						} else if ( export_header.parent().find( '.snapshot-export-failure' ).length ) {
							backup_complete_msg = snapshot_messages.export_backup_failure;
							backup_complete_icon = 'error';
						} else {
							backup_complete_msg = snapshot_messages.export_backup_success;
							backup_complete_icon = 'success';
						}
						jQuery( window ).trigger( 'snapshot:show_top_notice', [ backup_complete_icon, backup_complete_msg ] );
						row.trigger( 'click' );
					}, 3000 );
				} else {
					row.data( 'updating', false );
				}
			},
			error() {
				row.data( 'updating', false );
			}
		} );
	}

	/**
	 * Checks if Snapshot IPs are allowlisted.
	 *
	 * @param {Event} e
	 * @return {Promise}
	 */
	function are_ips_allowlisted( e ) {
		const obj = {
			method: 'GET',
			action: 'snapshot-check_ips',
			_wpnonce: $( '#_wpnonce-snapshot_backup_create_manual' ).val()
		};
		const resp = Helper.deferred();

		const request = Helper.request( obj );
		request
			.then( ( response ) => response.json() )
			.then( ( res ) => {
				if ( res.success ) {
					resp.resolve();
				}
				resp.reject();
			} )
			.catch( ( err ) => {
				resp.reject();
			} );

		return resp;
	}

	function handle_create_manual_backup( e, auto = false ) {
		if ( e && e.preventDefault ) {
			e.preventDefault();
		}
		const form = $( '#form-snapshot-create-manual-backup' );
		form.find( '.sui-button' ).addClass( 'sui-button-onload-text', true );
		$( '.button-create-backup' ).prop( 'disabled', true );

		const data = {};
		form.serializeArray().forEach( function( item ) {
			data[ item.name ] = item.value;
		} );

		if ( ! auto && 'on' === data.run_pre_check ) {
			Preflight.preflightReset();
			SUI.openModal( 'snapshot-modal-preflight-check', window );
			document.getElementById( 'modal-snapshot-create-manual-backup' ).closest( '.sui-modal' ).classList.remove( 'sui-active' );
			Preflight.preflightProgress();
		} else {
			const request_data = {
				action: 'snapshot-trigger_backup',
				_wpnonce: $( '#_wpnonce-snapshot_backup_create_manual' ).val(),
				data: {
					backup_name: data.backup_name,
					description: data.backup_description,
					apply_exclusions: data.apply_exclusions === 'on'
				}
			};

			$.ajax( {
				type: 'POST',
				url: ajaxurl,
				data: request_data,
				beforeSend() {
					form.find( '.sui-button' ).addClass( 'sui-button-onload-text', true );
					$( '.button-create-backup' ).prop( 'disabled', true );
				},
				complete() {
					form.find( '.sui-button' ).removeClass( 'sui-button-onload-text', true );
					form.find( '#manual-backup-comment' ).val( '' );
				},
				success( response ) {
					if ( response.success && false !== response.data.backup_running ) {
						if ( $( '.snapshot-listed-backups .snapshot-row' ).length ) {
							jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'info', snapshot_messages.backup_is_in_progress ] );
						} else {
						// First backup.
							jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'info', snapshot_messages.manual_backup_success ] );
						}

						$( '.snapshot-listed-backups' ).show();
						$( '.snapshot-no-backups' ).hide();
						$( '.sui-box-header.snapshot-no-backups-title' ).hide();
						$( '.sui-box-header.snapshot-has-backups-title' ).show();

						$( '.snapshot-listed-backups .sui-table > tbody:last-child' ).prepend( response.data.backup_running );
						snapshot_backup_progress( '1', 'just_triggered' );
					} else if ( response.data && Array.isArray( response.data.messages ) && response.data.messages.length ) {
						response.data.messages.forEach( function( message ) {
							const message_key = 'manual_' + message;
							const notice = $( '<span></span>' ).html( snapshot_messages[ message_key ] );
							notice.find( 'a' ).eq( 0 ).on( 'click', function() {
								notice.trigger( 'snapshot:close_notice' );
								toggle_navbar( 'logs' );
							} );
							notice.find( 'a' ).eq( 1 ).on( 'click', function() {
								notice.trigger( 'snapshot:close_notice' );
								jQuery( window ).trigger( 'snapshot:backup_modal' );
							} );
							jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', notice ] );
						} );
					} else {
						if ( 'status' in response.data && response.data.status === 'http_auth_required' ) {
							Helpers.Notice.close( 'auth_required_notice' );
							$( window ).trigger( 'snapshot:close_modal' );
							Helpers.Notice.open( {
								id: 'auth_required_notice',
								type: 'error',
								dismissible: true,
								message: SnapshotAjax.auth.notice
							} );

							return;
						}

						jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.manual_backup_error ] );
					}
					$( window ).trigger( 'snapshot:close_modal' );
				}
			} );
		}

		/**
		  // Check if IPs are allowlisted.
		  var allowlisted = are_ips_allowlisted(e);
		  allowlisted
		  .then(() => {
		  
		  })
		  .catch(() => {
		  $('.button-create-backup').prop('disabled', false);
		  form.find('.sui-button').removeClass('sui-button-onload-text');
		  SUI.closeModal();
		  var notice = $('<span></span>').html(snapshot_messages.running_backup_fail);
		  
		  setTimeout(() => {
		  $(window).trigger('snapshot:show_top_notice', ['error', notice]);
		  var snippet = notice.find('.sui-code-snippet');
		  if (snippet) {
		  snippet.SUICodeSnippet({
		  copyText: '<span class="sui-icon-copy" aria-hidden="true"></span>'
		  });
		  snippet.closest('.sui-code-snippet-wrapper').find('.sui-button').removeClass('sui-button').addClass('sui-button-icon');
		  }
		  }, 500);
		  
		  return;
		  });
		 */
	}

	function delete_backup( e, backup ) {
		e = e || false;
		if ( e && e.preventDefault ) {
			e.preventDefault();
		}

		const delete_buttons = $( '.snapshot-last-backup .snapshot-delete-backup .sui-button-icon' );
		call_if_can_delete_backup( delete_backup_confirm.bind( this, backup ), function() {
			delete_buttons.prop( 'disabled', true );
		}, function() {
			if ( backup ) {
				// If we have a backup pass it over to the delete backup modal.
				$( '#snapshot-confirm-wpmudev-password-modal' ).find( '[name=what]' ).val( `delete_backup_${ backup }` );
			}
			delete_buttons.prop( 'disabled', false );
		} );
	}

	function delete_backup_confirm( backup ) {
		SUI.openModal( 'snapshot-modal-delete-backup', window );
		$( '#snapshot-modal-delete-backup' ).data( 'backupId', backup );
		$( '#snapshot-delete-backup-button' ).removeClass( 'sui-button-onload-text' ).addClass( 'sui-button-red' );
		$( '#snapshot-modal-delete-backup-error-notice' ).hide();
		return false;
	}

	function delete_backup_confirmed() {
		const backup = $( '#snapshot-modal-delete-backup' ).data( 'backupId' );
		delete_backup_force( backup );
	}

	function delete_backup_force( backup ) {
		const delete_buttons = $( '.snapshot-last-backup .snapshot-delete-backup .sui-button-icon, #snapshot-settings-delete-backups-confirm' );
		const modal_button = $( '#snapshot-delete-backup-button' );

		delete_buttons.prop( 'disabled', true );
		modal_button.addClass( 'sui-button-onload-text' ).removeClass( 'sui-button-red' );
		$( '#snapshot-modal-delete-backup-error-notice' ).hide();

		const request_data = {
			action: 'snapshot-delete_backup',
			_wpnonce: $( '#_wpnonce-delete-backup' ).val(),
			data: {
				backup_id: backup,
			}
		};

		$.ajax( {
			type: 'POST',
			url: ajaxurl,
			data: request_data,
			success( response ) {
				if ( response.success ) {
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'info', snapshot_messages.settings_delete_success, 3000, false ] );

					const Promise = $.when( snapshot_list_backups() );
					Promise.done( function() {
						snapshot_backup_progress();
					} );

					const log_row = $( '.logs-list .log-row[data-backup-id="' + backup + '"]' );
					log_row.next().remove();
					log_row.remove();
					if ( ! $( '.logs-list .log-row' ).length ) {
						$( '.snapshot-logs .logs-empty' ).show();
						$( '.snapshot-logs .logs-not-empty' ).hide();
					}
					$( window ).trigger( 'snapshot:close_modal' );
				} else {
					$( '#snapshot-modal-delete-backup-error-notice' ).show();
				}
			},
			error() {
				$( '#snapshot-modal-delete-backup-error-notice' ).show();
			},
			complete() {
				delete_buttons.prop( 'disabled', false );
				modal_button.removeClass( 'sui-button-onload-text' ).addClass( 'sui-button-red' );
			}
		} );
	}

	function export_backup( e, backup ) {
		if ( e && e.preventDefault ) {
			e.preventDefault();
		}

		const request_data = {
			action: 'snapshot-export_backup',
			_wpnonce: $( '#_wpnonce-export-backup' ).val(),
			backup_id: backup
		};

		$.ajax( {
			type: 'POST',
			url: ajaxurl,
			data: request_data,
			success( response ) {
				if ( response.success ) {
					if ( 'api_response' in response.data && 'message' in response.data.api_response ) {
						if ( 'Export already in progress, please try after some time' === response.data.api_response.message ) {
							var notice = $( '<span></span>' ).html( snapshot_messages.backup_export_already_requested.replace( '%s', response.data.site ) );
                    		jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'info', notice ] );
						}
					} else {
						var notice = $( '<span></span>' ).html( snapshot_messages.backup_export_success.replace( '%s', response.data.site ) );
                    	jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'success', notice ] );
					}
				} else {
					var notice = $( '<span></span>' ).html( snapshot_messages.backup_export_error );
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', notice ] );
				}
			},
			error() {
				const notice = $( '<span></span>' ).html( snapshot_messages.backup_export_error );
				jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', notice ] );
			}
		} );
	}

	function cancel_backup_confirm() {
		const backup_row = $( this ).closest( 'table' ).find( '.current-backup-row' );
		const backup_id = backup_row.data( 'backupId' );
		$( '#snapshot-modal-cancel-backup #snapshot-cancel-backup' ).data( 'backupId', backup_id );
		$( '#snapshot-modal-cancel-backup #snapshot-cancel-backup' ).prop( 'disabled', false );
		SUI.openModal( 'snapshot-modal-cancel-backup', this );
	}

	function cancel_backup() {
		const backup_id = $( this ).data( 'backupId' );
		const data = {};

		data._wpnonce = $( '#_wpnonce-snapshot_cancel_backup' ).val();
		data.backup_id = backup_id;

		const url = ajaxurl + '?action=snapshot-cancel_backup';

		$.ajax( {
			type: 'POST',
			url,
			data,
			cache: false,
			dataType: 'json',
			beforeSend() {
				$( '#snapshot-modal-cancel-backup .snapshot-cancel-backup-cancel' ).prop( 'disabled', true );
				$( '#snapshot-modal-cancel-backup #snapshot-cancel-backup' ).prop( 'disabled', true );
				$( '#snapshot-modal-cancel-backup #snapshot-cancel-backup' ).addClass( 'sui-button-onload-text' );
			},
			success( result ) {
				if ( ! result.success ) {
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.cancel_backup_error ] );
				}
			},
			error() {
				$( window ).trigger( 'snapshot:close_modal' );
				jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.cancel_backup_error ] );
			}
		} );

		return false;
	}

	function filter_log() {
		const select = $( this );
		const container = select.closest( '.sui-box' ).find( '.log-items-container' );
		const no_warning = container.find( '.no-warning' );
		const no_error = container.find( '.no-error' );
		const log_items = container.find( '.log-lists > .log-item' );
		let show_items = log_items;
		const value = select.val();

		no_warning.hide();
		no_error.hide();
		log_items.hide();

		switch ( value ) {
			case 'all':
				break;
			case 'warning':
				show_items = log_items.filter( '.log-level-warning, .log-level-error' );
				if ( ! show_items.length ) {
					no_warning.show();
				}
				break;
			case 'error':
				show_items = log_items.filter( '.log-level-error' );
				if ( ! show_items.length ) {
					no_error.show();
				}
				break;
		}

		show_items.show();
	}

	function view_backup( backup_id ) {
		const row = $( '.snapshot-listed-backups .snapshot-row[data-backup_id="' + backup_id + '"]' );
		if ( row.length ) {
			toggle_navbar( 'backups' );
			if ( ! row.hasClass( 'sui-accordion-item--open' ) ) {
				row.trigger( 'click' );
			}
			$( [ document.documentElement, document.body ] ).animate( {
				scrollTop: row.offset().top - 35
			}, 'slow' );
		} else {
			jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'warning', snapshot_messages.log_backup_not_found ] );
		}
	}

	function view_log( e, force_reload, backup_id ) {
		const $this = $( this );
		if ( force_reload === undefined ) {
			force_reload = false;
		}
		if ( ! backup_id ) {
			backup_id = $this.data( 'backupId' );
		}
		reload_logs( force_reload ).done( function() {
			const row = $( '.logs-list .log-row[data-backup-id="' + backup_id + '"]' );
			if ( row.length ) {
				toggle_navbar( 'logs' );
				if ( ! row.hasClass( 'sui-accordion-item--open' ) ) {
					row.trigger( 'click' );
				}
				$( [ document.documentElement, document.body ] ).animate( {
					scrollTop: row.offset().top - 35
				}, 'slow' );
			} else {
				jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'warning', snapshot_messages.backup_log_not_found ] );
			}
		} );
	}

	function on_log_row_click() {
		const row = $( this );
		const content = row.next();
		if ( content.data( 'loaded' ) ) {
			return;
		}
		Log.single( this );
	}

	function reload_logs( force ) {
		if ( force === undefined ) {
			force = true;
		}

		const deferred = jQuery.Deferred();

		if ( ! force ) {
			const logs_loaded = $( '.snapshot-page-main .logs-list' ).data( 'logsLoaded' );
			if ( logs_loaded ) {
				deferred.resolve();
				return deferred.promise();
			}
		}

		jQuery.ajax( {
			type: 'GET',
			url: ajaxurl,
			data: {
				action: 'snapshot-get_log_list',
				_wpnonce: $( '#_wpnonce-get-backup-log' ).val()
			},
			cache: false,
			dataType: 'json',
			beforeSend() {
				$( '.snapshot-logs .logs-empty' ).hide();
				$( '.snapshot-logs .logs-not-empty' ).hide();
				$( '.logs-list .logs-loading' ).show();
			},
			complete() {
				$( '.logs-list .logs-loading' ).hide();
			},
			success( data ) {
				if ( data.success ) {
					$( '.logs-list' ).data( 'logsLoaded', true );
					$( '.snapshot-logs .log-rows' ).html( data.data.content );
					$( '.logs-list .log-row' ).on( 'click', on_log_row_click );
					$( '.logs-list .log-rows select' ).each( function() {
						SUI.select.init( $( this ) );
					} );
					if ( data.data.show_log ) {
						$( '.snapshot-logs .logs-empty' ).hide();
						$( '.snapshot-logs .logs-not-empty' ).show();
					} else {
						$( '.snapshot-logs .logs-empty' ).show();
						$( '.snapshot-logs .logs-not-empty' ).hide();
					}
					fix_export_destination_tooltips();
					deferred.resolve();
				} else {
					deferred.reject();
				}
			},
			error() {
				deferred.reject();
			}
		} );
		return deferred.promise();
	}

	/**
	 * Open log modal for running backup and start continuous update
	 *
	 * @param e
	 * @param backup_id
	 */
	function open_log_modal( e, backup_id ) {
		const modal = $( '#snapshot-modal-log' );
		if ( ! modal.length ) {
			return;
		}

		modal.find( '.sui-box-header .sui-button-icon' ).on( 'click', function() {
			update_log_offset = false;
			$( window ).trigger( 'snapshot:close_modal' );
		} );

		SUI.openModal( 'snapshot-modal-log', this );

		update_log_ajax( backup_id, 0, true, true );
	}

	/**
	 * Update log modal for running backup with new log entries which are after offset
	 *
	 * @param backup_id
	 * @param offset
	 * @param clear
	 * @param show_loader
	 */
	function update_log_ajax( backup_id, offset, clear, show_loader ) {
		$.ajax( {
			type: 'GET',
			url: ajaxurl,
			data: {
				action: 'snapshot-get_backup_log',
				backup_id,
				offset,
				_wpnonce: $( '#_wpnonce-get-backup-log' ).val()
			},
			cache: false,
			dataType: 'json',
			success( data ) {
				if ( data.success ) {
					update_log( data.data.log, clear, show_loader );
				} else {
					// Hide loader if error.
					update_log( null, false, false );
				}
			},
			error() {
				// Hide loader if error.
				update_log( null, false, false );
			}
		} );
	}

	/**
	 * Update log modal for running backup with new log entries from log.items and scroll down.
	 *
	 * @param log
	 * @param clear
	 * @param show_loader
	 */
	function update_log( log, clear, show_loader ) {
		const modal = $( '#snapshot-modal-log' );
		const log_container = modal.find( '.log-container' );
		const frame = log_container.closest( '.sui-border-frame' );

		if ( clear ) {
			log_container.empty();
			// Offset for next updating
			update_log_offset = log ? log.size : false;

			log_prev_scroll = 0;
			log_allow_auto_scroll = true;
			frame.off( 'scroll' );
			frame.on( 'scroll', function() {
				const scrollTop = frame.prop( 'scrollTop' );
				const bottom_scroll_offset = frame.prop( 'scrollHeight' ) - frame.prop( 'clientHeight' ) - scrollTop;
				if ( scrollTop < log_prev_scroll ) {
					log_allow_auto_scroll = false;
				}
				if ( bottom_scroll_offset <= 2 ) {
					log_allow_auto_scroll = true;
				}
				log_prev_scroll = scrollTop;
			} );
		}

		// Remove loader.
		log_container.find( '>p.log-item:last-child span.sui-icon-loader' ).remove();

		if ( log && log.items ) {
			// Update log in modal.
			const items = log.items;
			items.reverse().forEach( function( item ) {
				const log_item = $( '<p class="log-item"></p>' );
				log_item.addClass( 'log-level-' + item.level );
				log_item.text( item.message );
				log_item.appendTo( log_container );
			} );
		}

		if ( show_loader && update_log_offset !== false ) {
			// Show loader in the last log row.
			log_container.find( '>p.log-item:last-child' )
				.append( '<span class="sui-icon-loader sui-loading sui-md" aria-hidden="true"></span>' );
		}

		// Autoscrolling only if scrollbar is already at the bottom.
		if ( log_allow_auto_scroll ) {
			frame.animate( { scrollTop: frame.prop( 'scrollHeight' ) - frame.prop( 'clientHeight' ) }, 500 );
		}
	}

	function view_log_in_modal() {
		const backup_row = $( this ).closest( 'table' ).find( '.current-backup-row' );
		const backup_id = backup_row.data( 'backupId' );
		$( window ).trigger( 'snapshot:open_log_modal', [ backup_id ] );
	}

	function goto_backups_settings() {
		$( window ).trigger( 'snapshot:close_modal' );
		toggle_navbar( 'settings' );
		return false;
	}

	function restore_backup( e, backup_id ) {
		e = e || false;
		if ( e && e.preventDefault ) {
			e.preventDefault();
		}

		const form = $( '#form-snapshot-restore-backup' );
		form.find( 'input[name=backup_id]' ).val( backup_id );
		form.find( 'input[name=restore_rootpath]' ).val( snapshot_default_restore_path.path );
		SUI.openModal( 'modal-snapshot-restore-backup', this );
	}

	function show_region_confirmation_modal() {
		call_if_can_delete_backup( show_region_confirmation_modal_force, function() {
			$( '.snapshot-region-loading' ).show();
			$( '.snapshot-region-radio' ).hide();
		}, function() {
			revert_storage_radio();
			$( '.snapshot-region-loading' ).hide();
			$( '.snapshot-region-radio' ).show();
		} ).then( function() {
			revert_storage_radio();
		} );
		return false;
	}

	function show_region_confirmation_modal_force() {
		SUI.openModal( 'modal-backups-region-change', window );

		// Remove and re-add listener to the cancel buttons.
		$( '#modal-backups-region-change .cancel-region-change' ).off( 'click' );
		$( '#modal-backups-region-change .cancel-region-change' ).on( 'click', function() {
			// Lets cancel the change and close the modal.
			revert_storage_radio();
			$( window ).trigger( 'snapshot:close_modal' );
		} );

		// Deal with the region change.
		$( '#snapshot-backups-change-region' ).off( 'click' );
		$( '#snapshot-backups-change-region' ).on( 'click', function() {
			const data = {};

			data._wpnonce = $( '#_wpnonce-snapshot_change_region' ).val();
			data.no_backups = $( '.snapshot-backups-number .sui-summary-large' ).text() === '0' ? '1' : '0';
			data.new_region = $( 'input[name="snapshot-backup-region"]:checked' ).val();

			const url = ajaxurl + '?action=snapshot_change_region';

			$.ajax( {
				type: 'POST',
				url,
				data,
				cache: false,
				dataType: 'json',
				beforeSend() {
					$( '#modal-backups-region-change .sui-button' ).prop( 'disabled', true );
				},
				complete() {
					$( '#modal-backups-region-change .sui-button' ).prop( 'disabled', false );
					$( window ).trigger( 'snapshot:close_modal' );
				},
				success( response ) {
					if ( response.success ) {
						snapshot_list_backups();

						if ( true !== response.data.changed_schedule ) {
							// Show notice prompting to run backup or set schedule.
							var notice = $( '<span></span>' ).html( snapshot_messages.change_region_no_schedule );
							const a = notice.find( 'a' );
							a.eq( 0 ).on( 'click', function( e ) {
								notice.trigger( 'snapshot:close_notice' );
								jQuery( window ).trigger( 'snapshot:backup_modal' );
							} );
							a.eq( 1 ).on( 'click', function( e ) {
								notice.trigger( 'snapshot:close_notice' );
								$( '#snapshot-backup-schedule > a' ).trigger( 'click' );
							} );

							jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'info', notice ] );
						} else {
							// Show notice informing that schedule is active already.
							const schedule = $( '#snapshot-backup-schedule' ).data( 'modalData' );
							var notice = $( '<span></span>' ).html( snapshot_messages.change_region_with_schedule.replace( '%s', schedule.frequency_human ) );
							jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'info', notice ] );
						}
					} else {
						revert_storage_radio();

						var notice = $( '<span></span>' ).html( snapshot_messages.change_region_failure );
						jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', notice ] );
					}
				},
				error() {
					revert_storage_radio();

					const notice = $( '<span></span>' ).html( snapshot_messages.change_region_failure );
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', notice ] );
				}
			} );

			return false;
		} );

		return false;
	}

	function revert_storage_radio() {
		const selected_value = $( 'input[name="snapshot-backup-region"]:checked' ).val();
		let unselected_value = '';
		if ( selected_value === 'US' ) {
			unselected_value = 'EU';
		} else {
			unselected_value = 'US';
		}
		$( "input[name='snapshot-backup-region'][value='" + unselected_value + "']" ).prop( 'checked', true );
		$( "input[name='snapshot-backup-region'][value='" + selected_value + "']" ).prop( 'checked', false );
	}

	function delete_backups_confirm() {
		call_if_can_delete_backup( delete_backups_confirm_force, function() {
			$( '#snapshot-settings-delete-backups-confirm' ).addClass( 'sui-button-onload' );
		}, function() {
			$( '#snapshot-settings-delete-backups-confirm' ).removeClass( 'sui-button-onload' );
		} );
		return false;
	}

	function delete_backups_confirm_force() {
		SUI.openModal( 'modal-settings-delete-backups', window );
		return false;
	}

	function delete_backups() {
		const data = {};

		data._wpnonce = $( '#_wpnonce-snapshot_delete_all_backups' ).val();

		const url = ajaxurl + '?action=snapshot_delete_all_backups';

		$.ajax( {
			type: 'POST',
			url,
			data,
			cache: false,
			dataType: 'json',
			beforeSend() {
				$( '#modal-settings-delete-backups .sui-button' ).prop( 'disabled', true );
			},
			complete() {
				$( '#modal-settings-delete-backups .sui-button' ).prop( 'disabled', false );
				$( window ).trigger( 'snapshot:close_modal' );
				$.when( snapshot_list_backups() ).done( function() {
					snapshot_backup_progress();
				} );
			},
			success( data ) {
				if ( data.success ) {
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'success', snapshot_messages.delete_all_backups_success, 3000, false ] );
				} else {
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.delete_all_backups_error ] );
				}
			},
			error() {
				jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.delete_all_backups_error ] );
			}
		} );

		return false;
	}

	function backup_is_cancelled() {
		$( '.button-create-backup' ).prop( 'disabled', false );
		jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.cancel_backup_success, 3000, false ] );

		const cancelModal = $( '#snapshot-modal-cancel-backup' ).parent();
		cancelModal.find( '.sui-button' ).prop( 'disabled', false );
		$( '#snapshot-modal-cancel-backup #snapshot-cancel-backup' ).removeClass( 'sui-button-onload-text' );
		if ( cancelModal.hasClass( 'sui-active' ) ) {
			$( window ).trigger( 'snapshot:close_modal' );
		}
		snapshot_list_backups();
	}

	function recheck_requirements() {
		const data = {};
		data._wpnonce = $( '#_wpnonce-snapshot_recheck_requirements' ).val();

		$.ajax( {
			type: 'POST',
			url: ajaxurl + '?action=snapshot-recheck_requirements',
			data,
			beforeSend() {
				$( '#modal-snapshot-requirements-check-failure .snapshot-recheck-requirements' ).addClass( 'sui-button-onload-text', true );
			},
			complete() {
				$( '#modal-snapshot-requirements-check-failure .snapshot-recheck-requirements' ).removeClass( 'sui-button-onload-text', true );
			},
			success( response ) {
				if ( response.success ) {
					$( '#snapshot-php-version' ).val( response.data.compat_php_version );
					if ( response.data.compat_php_version >= 0 ) {
						$( window ).trigger( 'snapshot:close_modal' );
						SUI.openModal( 'modal-snapshot-requirements-check-success', 'button-create-backup' );
					}
				}
			}
		} );
	}

	function populate_snapshot_region() {
		const data = {};
		data._wpnonce = $( '#_wpnonce-populate_snapshot_region' ).val();

		$.ajax( {
			type: 'POST',
			url: ajaxurl + '?action=snapshot-check_creds',
			data,
			complete() {
				$( '.snapshot-region-loading' ).hide();
				$( '.snapshot-storage-limit-loading' ).hide();
				$( '.snapshot-region-radio' ).show();
				$( '.snapshot-storage-limit-input' ).show();
			},
			success( response ) {
				if ( response.success ) {
					if ( response.data.region === 'US' ) {
						$( '#backup-region-us' ).prop( 'checked', true );
						$( '#backup-region-eu' ).prop( 'checked', false );
					}
					if ( response.data.region === 'EU' ) {
						$( '#backup-region-eu' ).prop( 'checked', true );
						$( '#backup-region-us' ).prop( 'checked', false );
					}

					if ( response.data.rotation_frequency ) {
						$( '#snapshot-backup-limit' ).val( response.data.rotation_frequency );
						$( '#existing_backup_limit' ).val( response.data.rotation_frequency );
					}
				}
			}
		} );
	}

	function requirements_passed() {
		$( window ).trigger( 'snapshot:close_modal' );
		jQuery( window ).trigger( 'snapshot:backup_modal' );
	}

	function toggle_notification_recipients() {
		const checkbox = $( this );
		const content = checkbox.closest( '.sui-form-field' ).find( '.sui-toggle-content' );
		checkbox.prop( 'checked' ) ? content.show() : content.hide();
	}

	function reset_tables_search() {
		document.getElementById( 'tables-search-text-result' ).style.display = 'none';
		document.getElementById( 'tables-search-text-no-result' ).style.display = 'none';
		document.getElementById( 'snapshot-tables-search' ).value = '';
		document.querySelectorAll( '.search_hide' ).forEach( function( el ) {
			el.classList.remove( 'search_hide' );
		} );
	}

	function reset_tables_selection() {
		reset_tables_search();

		document.querySelectorAll( '.snapshot-tables-listitem' ).forEach( function( el ) {
			if ( -1 === el.dataset.table_name.search( 'defender_lockout_log' ) ) {
				el.setAttribute( 'aria-selected', false );
			} else {
				el.setAttribute( 'aria-selected', true );
			}
		} );

		document.querySelectorAll( '.table_category' ).forEach( function( el ) {
			el.setAttribute( 'aria-selected', false );
		} );

		document.getElementById( 'main-item' ).setAttribute( 'aria-selected', false );
	}

	function search_db_tables() {
		const search_input_val = document.getElementById( 'snapshot-tables-search' ).value;
		if ( search_input_val.length > 1 ) {
			const table_category_lis = document.querySelectorAll( '.table_category' );
			let result_flag = false;
			table_category_lis.forEach( function( table_category_li ) {
				let category_flag = false,
					tables_nodes = table_category_li.querySelectorAll( '.snapshot-tables-listitem' );

				tables_nodes.forEach( function( table_node ) {
					const table_name = table_node.dataset.table_name;
					if ( -1 == table_name.search( search_input_val ) ) {
						table_node.classList.add( 'search_hide' );
					} else {
						category_flag = true;
						table_node.classList.remove( 'search_hide' );
					}
				} );

				if ( category_flag ) {
					table_category_li.classList.remove( 'search_hide' );
					result_flag = true;
				} else {
					table_category_li.classList.add( 'search_hide' );
				}
			} );

			if ( result_flag ) {
				document.getElementById( 'tables-search-text-no-result' ).style.display = 'none';
				document.getElementById( 'tables-search-text-result' ).style.display = 'block';
				document.getElementById( 'search-result' ).innerText = search_input_val;
				document.getElementById( 'main-item' ).classList.remove( 'search_hide' );
			} else {
				document.getElementById( 'tables-search-text-result' ).style.display = 'none';
				document.getElementById( 'tables-search-text-no-result' ).style.display = 'block';
				document.getElementById( 'search-no-result' ).innerText = search_input_val;
				document.getElementById( 'main-item' ).classList.add( 'search_hide' );
			}
		} else {
			document.getElementById( 'tables-search-text-result' ).style.display = 'none';
			document.getElementById( 'tables-search-text-no-result' ).style.display = 'none';
			const hidden_els = document.querySelectorAll( '.search_hide' );
			hidden_els.forEach( function( hidden_el ) {
				hidden_el.classList.remove( 'search_hide' );
			} );
		}
	}

	function handle_notifications_settings( e ) {
		if ( e && e.preventDefault ) {
			e.preventDefault();
		}

		const form = $( this );
		const data = {};
		form.serializeArray().forEach( function( item ) {
			data[ item.name ] = item.value;
		} );
		data.action = 'save_snapshot_settings';
		const recipients = [];
		form.find( '#snapshot-notification-recipients .sui-recipients .sui-recipient' ).each( function() {
			const recipient = $( this );
			recipients.push( {
				name: recipient.find( '.sui-recipient-name' ).text(),
				email: recipient.find( '.sui-recipient-email' ).text()
			} );
		} );
		data.email_settings = {
			on_fail_send: form.find( '#snapshot-notifications-send-email' ).prop( 'checked' ),
			on_fail_recipients: recipients,
			notify_on_fail: form.find( '#snapshot-backup-fails' ).prop( 'checked' ),
			notify_on_complete: form.find( '#snapshot-backup-completes' ).prop( 'checked' )
		};
		data.email_settings = JSON.stringify( data.email_settings );

		$.ajax( {
			type: 'POST',
			url: ajaxurl,
			data,
			beforeSend() {
				form.find( '.sui-button' ).addClass( 'sui-button-onload-text' ).prop( 'disabled', true );
			},
			complete() {
				form.find( '.sui-button' ).removeClass( 'sui-button-onload-text' ).prop( 'disabled', false );
			},
			success( response ) {
				if ( response.success ) {
					const notice = form.find( '.sui-notice.email-notification-notice' );
					notice.find( '.sui-notice-message p' ).text( response.data.notice_text );

					form.find( '.sui-recipients .sui-recipient' ).remove();
					response.data.email_settings.on_fail_recipients.forEach( function( item ) {
						add_recipient_html( item.name, item.email );
					} );
					form.find( '#snapshot-backup-fails' ).prop( 'checked', response.data.email_settings.notify_on_fail );
					form.find( '#snapshot-backup-completes' ).prop( 'checked', response.data.email_settings.notify_on_complete );

					if ( response.data.notice_type === 'success' ) {
						notice.addClass( 'sui-notice-success' );
					} else {
						notice.removeClass( 'sui-notice-success' );
						const checkbox = $( '#snapshot-notifications-send-email' );
						checkbox.prop( 'checked', response.data.email_settings.on_fail_send );
						const content = checkbox.closest( '.sui-form-field' ).find( '.sui-toggle-content' );
						response.data.email_settings.on_fail_send ? content.show() : content.hide();
						form.find( '.email-notification-notice-empty' ).hide();
					}
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'success', response.data.top_notice_text, 3000, false ] );
				}
			}
		} );
	}

	function remove_recipient( e ) {
		if ( e && e.preventDefault ) {
			e.preventDefault();
		}
		const recipient = $( this ).closest( '.sui-recipient' );
		const recipients = recipient.closest( '.sui-recipients' );
		const notice = recipients.find( '.email-notification-notice-empty' );
		recipient.remove();
		if ( ! recipients.find( '.sui-recipient' ).length ) {
			notice.show();
		}
	}

	function add_recipient_modal_hide_errors() {
		$( '#modal-notification-add-recipient-input-email-error' ).hide()
			.closest( '.sui-form-field' ).removeClass( 'sui-form-field-error' );
		$( '#modal-notification-add-recipient-input-email-duplicate-error' ).hide();
	}

	function add_recipient( e ) {
		if ( e && e.preventDefault ) {
			e.preventDefault();
		}
		$( '#modal-notification-add-recipient input:not([type=hidden])' ).val( '' );

		add_recipient_modal_hide_errors();

		SUI.openModal( 'modal-notification-add-recipient', this, 'modal-notification-add-recipient-input-name' );
	}

	function add_recipient_html( name, email ) {
		name = name.trim();
		email = email.trim();

		const recipients = $( '.snapshot-notifications #snapshot-notification-recipients .sui-recipients' );

		const recipient = $( '<div class="sui-recipient"></div>' );
		$( '<div class="sui-recipient-name"></div>' ).text( name ).appendTo( recipient );
		$( '<div class="sui-recipient-email"></div>' ).text( email ).appendTo( recipient );
		$( '<button type="button" class="sui-button-icon snapshot-remove-recipient"><span class="sui-icon-trash" aria-hidden="true"></span></button>' ).appendTo( recipient );
		recipient.appendTo( recipients );
		recipients.find( '.email-notification-notice-empty' ).hide();

		return name !== '' ? name : email;
	}

	function handle_add_recipient_form( e ) {
		if ( e && e.preventDefault ) {
			e.preventDefault();
		}

		const error_element = $( '#modal-notification-add-recipient-input-email-error' );
		const error_element2 = $( '#modal-notification-add-recipient-input-email-duplicate-error' );

		const form = $( this );
		const data = {};
		form.serializeArray().forEach( function( item ) {
			data[ item.name ] = item.value;
		} );

		const recipients = $( '.snapshot-notifications #snapshot-notification-recipients .sui-recipients' );
		error_element2.hide();
		let has_error = false;
		recipients.find( '.sui-recipient' ).each( function() {
			const recipient = $( this );
			if ( recipient.find( '.sui-recipient-email' ).text() === data.email.trim() ) {
				error_element2.show();
				error_element2.closest( '.sui-form-field' ).addClass( 'sui-form-field-error' );
				has_error = true;
				return false;
			}
		} );
		if ( has_error ) {
			return;
		}

		$.ajax( {
			type: 'POST',
			url: ajaxurl,
			data: {
				action: 'snapshot-json_validate_email',
				_wpnonce: data._wpnonce_snapshot_validate_email,
				email: data.email
			},
			beforeSend() {
				form.find( '.sui-button' ).addClass( 'sui-button-onload-text' ).prop( 'disabled', true );
			},
			complete() {
				form.find( '.sui-button' ).removeClass( 'sui-button-onload-text' ).prop( 'disabled', false );
			},
			success( response ) {
				if ( response.success && response.data.is_valid ) {
					const added = add_recipient_html( data.name, data.email );
					SUI.closeModal();
					add_recipient_modal_hide_errors();
					if ( added !== false ) {
						jQuery( window ).trigger( 'snapshot:show_top_notice', [
							'info',
							snapshot_messages.notifications_user_added.replace( '%s', added )
						] );
					}
				} else {
					error_element.show();
					error_element.closest( '.sui-form-field' ).addClass( 'sui-form-field-error' );
				}
			}
		} );
	}

	function history_replace_state() {
		if ( window.history && window.history.replaceState ) {
			window.history.replaceState( '', document.title, window.location.pathname + window.location.search );
		}
	}

	/**
	 * Check if current user can delete backups
	 */
	function check_can_delete_backup() {
		const deferred = $.Deferred();

		const request_data = {
			action: 'snapshot-check_can_delete_backup',
			_wpnonce: $( '#_wpnonce-check_can_delete_backup' ).val()
		};

		$.ajax( {
			type: 'POST',
			url: ajaxurl,
			data: request_data,
			cache: false,
			success( response ) {
				if ( response.success && response.data.can_delete_backup === true ) {
					deferred.resolve();
				} else {
					deferred.reject();
				}
			},
			error() {
				deferred.reject();
			}
		} );

		return deferred.promise();
	}

	/**
	 * Show Confirm WPMU DEV password modal
	 *
	 * @param {Function} callback
	 */
	function confirm_wpmudev_password( callback ) {
		const deferred = $.Deferred();
		$( '#snapshot-confirm-wpmudev-password-modal' ).data( 'on_success', [ deferred, callback ] );
		$( '#error-snapshot-wpmudev-password' ).hide();
		$( '#error-snapshot-wpmudev-password' ).closest( '.sui-form-field' ).removeClass( 'sui-form-field-error' );
		$( '#snapshot-wpmudev-password' ).val( '' );

		const cbString = callback.toString(); // Convert the callback function to string.

		if ( cbString.includes( 'modal-backups-region-change' ) ) {
			let region = $( '[name=snapshot-backup-region]:checked' ).val();

			if ( 'US' === region ) {
				region = 'EU';
			} else {
				region = 'US';
			}
			$( '#snapshot-confirm-wpmudev-password-modal' ).find( '[name=what]' ).val( `change_region_${ region }` );
		} else if ( cbString.includes( 'modal-settings-delete-backups' ) ) {
			$( '#snapshot-confirm-wpmudev-password-modal' ).find( '[name=what]' ).val( 'delete_backups' );
		}

		SUI.openModal( 'snapshot-confirm-wpmudev-password-modal', window );
		return deferred.promise();
	}

	/**
	 * Check WPMU DEV password
	 *
	 * @param {string} wpmudev_password
	 */
	function validate_wpmudev_password( wpmudev_password ) {
		const deferred = $.Deferred();

		const request_data = {
			action: 'snapshot-check_wpmudev_password',
			_wpnonce: $( '#_wpnonce-check_wpmudev_password' ).val(),
			wpmudev_password
		};

		$.ajax( {
			type: 'POST',
			url: ajaxurl,
			data: request_data,
			cache: false,
			success( response ) {
				if ( response.success && response.data.password_is_valid === true ) {
					deferred.resolve();
				} else {
					deferred.reject( response.data );
				}
			},
			error() {
				deferred.reject();
			}
		} );

		return deferred.promise();
	}

	/**
	 * Confirm WPMU DEV password modal: form submit handler
	 */
	function on_submit_wpmudev_password() {
		const form = $( '#snapshot-confirm-wpmudev-password-modal-form' );
		const password = form.find( '[name=wpmudev_password]' ).val();

		const on_callback = $( '#snapshot-confirm-wpmudev-password-modal' ).data( 'on_success' );

		if ( on_callback ) {
			const deferred = on_callback[ 0 ];
			const callback = on_callback[ 1 ];

			const button = form.find( '.submit-button' );
			button.addClass( 'sui-button-onload' );
			validate_wpmudev_password( password ).then( function() {
				SUI.closeModal();
				if ( typeof callback === 'function' ) {
					const result = callback();
					deferred.resolve( result );
				}
				$( '#error-snapshot-wpmudev-password' ).hide();
				$( '#error-snapshot-wpmudev-password' ).closest( '.sui-form-field' ).removeClass( 'sui-form-field-error' );
			}, function( err ) {
				//deferred.reject();
				if ( err.code === 'invalid_login_method' ) {
					$( '#snapshot-wpmudev-password' ).attr( 'disabled', 'disabled' );
					$( '#snapshot-wpmudev-password' ).addClass( 'sui-disabled' );
					$( '#snapshot-confirm-wpmudev-password-modal' ).find( '.snapshot-login--method__error' ).removeClass( 'd-none' );
					$( '#snapshot-confirm-wpmudev-password-modal' ).find( '.snapshot-login--method__error p' ).html( snapshot_messages.auth_method_invalid );
				} else {
					$( '#error-snapshot-wpmudev-password' ).show();
					$( '#error-snapshot-wpmudev-password' ).closest( '.sui-form-field' ).addClass( 'sui-form-field-error' );
					$( '#snapshot-wpmudev-password' ).focus();
				}
			} ).always( function() {
				button.removeClass( 'sui-button-onload' );
			} );
		}

		return false;
	}

	/**
	 * Run callback function and ask for WPMU DEV password if needed
	 *
	 * @param {Function} callback
	 * @param            before
	 * @param            after
	 */
	function call_if_can_delete_backup( callback, before, after ) {
		const deferred = $.Deferred();

		if ( typeof before === 'function' ) {
			before();
		}

		check_can_delete_backup().then( function() {
			if ( typeof after === 'function' ) {
				after();
			}
			const result = callback();
			deferred.resolve( result );
		}, function() {
			if ( typeof after === 'function' ) {
				after();
			}
			confirm_wpmudev_password( callback ).then( function( data ) {
				deferred.resolve( data );
			}, function() {
				deferred.reject();
			} );
		} );

		return deferred.promise();
	}

	/**
	 * Change the storage limit in the API.
	 */
	function change_storage_limit() {
		$( '#error-snapshot-backup-limit' ).hide();
		$( '#error-snapshot-backup-limit' ).html( '' );
		$( '.snapshot-storage-limit-input .sui-form-field' ).removeClass( 'sui-form-field-error' );

		const request_data = {
			action: 'snapshot_change_storage_limit',
			_wpnonce: $( '#_wpnonce-snapshot_change_storage_limit' ).val(),
			storage_limit: $( '#snapshot-backup-limit' ).val()
		};

		if ( ! $.isNumeric( request_data.storage_limit ) || request_data.storage_limit < 1 || request_data.storage_limit > 30 ) {
			$( '#error-snapshot-backup-limit' ).show();
			$( '#error-snapshot-backup-limit' ).html( snapshot_messages.storage_limit_invalid );
			$( '.snapshot-storage-limit-input .sui-form-field' ).addClass( 'sui-form-field-error' );
			$( '#snapshot-backup-limit' ).trigger( 'focus' );

			return false;
		}

		$.ajax( {
			type: 'POST',
			url: ajaxurl,
			data: request_data,
			beforeSend() {
				$( '#snapshot-backup-limit-button' ).addClass( 'sui-button-onload' );
			},
			complete() {
				$( '#snapshot-backup-limit-button' ).removeClass( 'sui-button-onload' );
			},
			success( response ) {
				if ( response.success && response.data.changed_storage ) {
					$( '#snapshot-backup-limit' ).trigger( 'focus' );
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'success', snapshot_messages.storage_limit_success ] );
				} else {
					$( '#snapshot-backup-limit' ).trigger( 'focus' );
					jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.storage_limit_failure ] );
				}
			},
			error() {
				$( '#snapshot-backup-limit' ).trigger( 'focus' );
				jQuery( window ).trigger( 'snapshot:show_top_notice', [ 'error', snapshot_messages.storage_limit_failure ] );
			}
		} );

		return false;
	}

	$( window ).on( 'load', function( e ) {
		let matches;
		if ( '#set-schedule' === window.location.hash ) {
			snapshot_get_schedule( true, $( '#snapshot-backup-schedule' ).data( 'modalData' ) );
			history_replace_state();
		} else if ( '#logs' === window.location.hash ) {
			// Open backup logs tab from URL.
			toggle_navbar( 'logs' );
			history_replace_state();
		} else if ( '#settings' === window.location.hash ) {
			// Open backup logs tab from URL.
			toggle_navbar( 'settings' );
			history_replace_state();
		} else if ( matches = window.location.hash.match( /^#logs\-(.+)/ ) ) {
			// Open backup log from URL.
			view_log( e, true, matches[ 1 ] );
			history_replace_state();
		} else if ( '#notifications' === window.location.hash ) {
			// Open notifications tab from URL.
			toggle_navbar( 'notifications' );
			history_replace_state();
		}
		Preflight.onLoad( handle_create_manual_backup );
	} );

	const urlSearchParams = new URLSearchParams( window.location.search );
	if ( urlSearchParams.has( 'referer' ) && 'google_login' === urlSearchParams.get( 'referer' ) ) {
		const action = urlSearchParams.get( 'action' );
		if ( urlSearchParams.has( 'set_apikey' ) && snapshotReact.requestsData.apiKey === urlSearchParams.get( 'set_apikey' ) ) {
			if ( action.indexOf( 'change_region' ) >= 0 ) {
				UI.switchTab( 'settings' );
				// Change the tab to settings.
				const region = action.split( 'change_region_' );
				const reg = region[ 1 ];

				setTimeout( () => {
					const radios = document.querySelectorAll( '[name=snapshot-backup-region]' );
					radios.forEach( ( el ) => {
						el.checked = false;
					} );

					document.querySelector( '#backup-region-' + reg.toLowerCase() ).checked = true;
					show_region_confirmation_modal_force();
				}, 3000 );
			} else if ( 'delete_backups' === action ) {
				UI.switchTab( 'settings' );
				delete_backups_confirm_force();
			} else if ( action.includes( 'delete_backup_' ) ) {
				// Get the backupId from the url.
				const backup = action.split( 'delete_backup_' );
				// Ask for the user confirmation.
				delete_backup_confirm( backup[ 1 ] );
			}
			window.history.pushState( null, '', snapshot_urls.backups );
		} else {
			// Show authentication failed notice.
			UI.displayNotice( {
				message: 'google_unlinked' == urlSearchParams.get( 'auth_error' ) ? snapshot_messages.google_auth_method_invalid : snapshot_messages.google_auth_failed,
				type: 'error',
				dismissible: true
			} );
			if ( action.includes( 'delete_backup' ) || action.includes( 'change_region' ) || action.includes( 'apply_config' ) ) {
				window.history.pushState( null, '', snapshot_urls.backups );
			}
		}
	}

	$( function() {
		if ( $( '.snapshot-page-backups' ).length ) {
			for ( const current_tab in navbar_tabs ) {
				const vertical_selector = navbar_tabs[ current_tab ][ 1 ];
				$( '.snapshot-page-main' ).find( vertical_selector )
					.on( 'click', toggle_navbar.bind( this, current_tab ) );
			}
			$( '.snapshot-page-main .sui-mobile-nav' ).on( 'change', function() {
				toggle_navbar( $( this ).val() );
			} );

			$( '#snapshot-backup-schedule > a' ).on( 'click', function() {
				snapshot_get_schedule( true, $( this ).parent().data( 'modalData' ) );
				return false;
			} );

			$( window ).on( 'snapshot:get-schedule', function() {
				snapshot_get_schedule( false );
			} );

			snapshot_get_schedule( false );

			$( '#form-snapshot-create-manual-backup' ).on( 'submit', handle_create_manual_backup );

			$( '.snapshot-list-backups' ).on( 'click', '.open-edit-schedule', function() {
				const data = $( this ).data( 'modal-data' );
				$( window ).trigger( 'snapshot:schedule', [ data.schedule_action, data.values, data.schedule_action ] );
			} );

			$( window ).on( 'snapshot:delete_backup', delete_backup );
			$( '#snapshot-delete-backup-button' ).on( 'click', delete_backup_confirmed );
			$( window ).on( 'snapshot:export_backup', export_backup );
			$( window ).on( 'snapshot:restore_backup', restore_backup );

			const Promise = $.when( snapshot_list_backups() );
			Promise.done( function() {
				snapshot_backup_progress( '1' );

				let matches;
				if ( matches = window.location.hash.match( /^#backups\-(.+)/ ) ) {
					// Open backup from URL.
					setTimeout( function() {
						view_backup( matches[ 1 ] );
					}, 100 );
					history_replace_state();
				}
			} );

			$( '#button-reload-backups' ).on( 'click', function() {
				$.when( snapshot_list_backups() ).done( function() {
					snapshot_backup_progress();
				} );
			} );

			$( '.snapshot-list-backups' ).on( 'click', '.button-cancel-backup', cancel_backup_confirm );
			$( '#snapshot-cancel-backup' ).on( 'click', cancel_backup );
			$( '.snapshot-list-backups' ).on( 'click', '.button-view-log', view_log_in_modal );

			$( '.logs-list' ).on( 'click', '.view-backup', function() {
				view_backup( $( this ).data( 'backupId' ) );
			} );

			$( '.logs-list .log-row' ).on( 'click', on_log_row_click );

			$( window ).on( 'snapshot:open_log_modal', open_log_modal );
			$( window ).on( 'snapshot:view_log', view_log );

			$( '#snapshot-button-backups-settings' ).on( 'click', goto_backups_settings );

			// For restoration modal log.
			$( window ).on( 'snapshot:update_log_ajax', function( event, backup_id, offset, clear, show_loader ) {
				update_log_ajax( backup_id, offset, clear, show_loader );
			} );
			$( window ).on( 'snapshot:update_log', function( event, log, clear, show_loader ) {
				update_log( log, clear, show_loader );
			} );
			$( window ).on( 'snapshot:update_log_offset', function( event, value ) {
				update_log_offset = value;
			} );
			$( window ).on( 'snapshot:get_log_offset', function( event, callback ) {
				callback( update_log_offset );
			} );

			// Show confirmation modal, when user changes the region in the settings.
			$( '#wps-settings input[name=snapshot-backup-region]' ).on( 'change', show_region_confirmation_modal );

			$( '#snapshot-region-notice' ).show();

			$( '#snapshot-settings-delete-backups-confirm' ).on( 'click', delete_backups_confirm );
			$( '#snapshot-settings-delete-backups' ).on( 'click', delete_backups );

			// Recheck Snapshot requirements (PHP version, etc.) when user clicks the recheck button.
			$( '#modal-snapshot-requirements-check-failure .snapshot-recheck-requirements' ).on( 'click', recheck_requirements );

			// Continue with the Create Backup modal, since we're covering all requirements.
			$( '#modal-snapshot-requirements-check-success .snapshot-checked-requirements' ).on( 'click', requirements_passed );

			$( '.snapshot-notifications input[aria-controls=snapshot-notification-recipients]' ).on( 'change', toggle_notification_recipients );
			$( '.snapshot-notifications #wps-notifications' ).on( 'submit', handle_notifications_settings );
			$( '.snapshot-notifications' ).on( 'click', '.snapshot-remove-recipient', remove_recipient );
			$( '.snapshot-notifications .snapshot-add-recipient' ).on( 'click', add_recipient );
			$( '#modal-notification-add-recipient-form' ).on( 'submit', handle_add_recipient_form );

			$( '#snapshot-default-exclusions' ).on( 'change', function() {
				if ( this.checked ) {
					$( '.snapshot-exclusions-settings-box .sui-accordion-item-body .sui-box-body' ).removeClass( 'snapshot-disabled-exclusions' );
				} else {
					$( '.snapshot-exclusions-settings-box .sui-accordion-item-body .sui-box-body' ).addClass( 'snapshot-disabled-exclusions' );
				}
			} );

			populate_snapshot_region();

			$( '#snapshot-confirm-wpmudev-password-modal-form' ).on( 'submit', on_submit_wpmudev_password );

			$( '#snapshot-backup-limit' ).on( 'focusin', function() {
				$( '#snapshot-backup-limit-button' ).prop( 'disabled', false );
			} ).on( 'focusout', function() {
				if ( ! $( '#snapshot-backup-limit-button:active' ).length && ! $( '#snapshot-backup-limit-button:hover' ).length ) {
					$( '#snapshot-backup-limit-button' ).prop( 'disabled', true );
				}
			} );

			$( '#snapshot-backup-limit-button' ).on( 'click', change_storage_limit );
			document.getElementById( 'snapshot-backup-limit' ).addEventListener( 'keydown', function( event ) {
				if ( event.key === 'Enter' ) {
					event.stopPropagation();
					event.preventDefault();
					change_storage_limit();
				}
			} );

			document.getElementById( 'snapshot-tables-search' ).addEventListener( 'keyup', search_db_tables );
			document.getElementById( 'snapshot-tables-search' ).addEventListener( 'keypress', function( event ) {
				if ( event.key === 'Enter' ) {
					event.preventDefault();
				}
			} );
			document.getElementById( 'reset-tables-search' ).addEventListener( 'click', reset_tables_selection );

			const tmplExportOptions = document.querySelector( '#tmpl-snapshot-backup-export-options' );
			const exportModal = document.querySelector( '#modal-snapshot-export-backup' );
			const exporting = false;

			$( document ).on( 'click', '.snapshot-export-backup', function( e ) {
				e.preventDefault();

				const backupId = e.target.dataset.snapshotId;
				const name = e.target.dataset.snapshotName;

				if ( exporting ) {
					// If there is already an export request running return early.
					return;
				}

				exportModal.querySelector( '#snapshot--backup__id' ).value = backupId;

				const checkActiveTab = exportModal.querySelector( '.sui-tab-item.active' );

				if ( 'send-mail__tab' === checkActiveTab.getAttribute( 'id' ) ) {
					exportModal.querySelector( '.snapshot-export--options__step__content' ).innerHTML = snapshot_messages.export_step_1;
					exportModal.querySelector( '.snapshot-email--tab__content' ).style.display = 'block';
					exportModal.querySelector( '.snapshot--send__email' ).classList.add( 'sui-hidden' );
					exportModal.querySelector( '#snapshot-export--move__ahead__button' ).classList.remove( 'sui-hidden' );
				}

				setTimeout( () => {
					SUI.openModal( 'modal-snapshot-export-backup', window );
				}, 50 );

				setTimeout( () => {
					const fillers = exportModal.querySelectorAll( '.snapshot-export--options__wrap' );
					fillers.forEach( ( filler ) => {
						filler.innerHTML = '';

						if ( filler.closest( '#direct-download__content' ) ) {
							filler.innerHTML = tmplExportOptions.innerHTML;
						}
					} );
				}, 200 );
			} );

			const sendMailTabBtn = exportModal.querySelector( '#send-mail__tab' );
			const directDownloadTabBtn = exportModal.querySelector( '#direct-download__tab' );
			const exportMoveToNextStep = exportModal.querySelector( '#snapshot-export--move__ahead__button' );

			if ( sendMailTabBtn ) {
				sendMailTabBtn.addEventListener( 'click', () => {
					const directDownload = exportModal.querySelector( '#direct-download__content' );
					const sendMail = exportModal.querySelector( '.snapshot-export--options__body' );

					directDownload.querySelector( '.snapshot-export--options__wrap' ).innerHTML = '';
					sendMail.querySelector( '.snapshot-export--options__wrap' ).innerHTML = '';
					sendMail.querySelector( '.snapshot-email--tab__content' ).style.display = 'block';
					exportModal.querySelector( '.snapshot-export--options__step__content' ).innerHTML = snapshot_messages.export_step_1;

					exportModal.querySelector( '.snapshot--send__email' ).classList.add( 'sui-hidden' );
					exportModal.querySelector( '#snapshot-export--move__ahead__button' ).classList.remove( 'sui-hidden' );
				} );
			}

			if ( directDownloadTabBtn ) {
				directDownloadTabBtn.addEventListener( 'click', () => {
					const directDownload = exportModal.querySelector( '#direct-download__content' );
					const sendMail = exportModal.querySelector( '#send-mail__content' );

					sendMail.querySelector( '.snapshot-export--options__wrap' ).innerHTML = '';

					directDownload.querySelector( '.snapshot-export--options__wrap' ).innerHTML = '';
					directDownload.querySelector( '.snapshot-export--options__wrap' ).innerHTML = tmplExportOptions.innerHTML;
				} );
			}

			if ( exportMoveToNextStep ) {
				exportMoveToNextStep.addEventListener( 'click', ( e ) => {
					e.preventDefault();
					const parentEl = e.target.closest( '#send-mail__content' );

					const sendToEmailRadios = parentEl.querySelectorAll( 'input[name="send-to-mail"]' );
					const emailAddress = parentEl.querySelector( 'input[name="email-address"]' );

					const sendToEmailValue = Array.from( sendToEmailRadios ).find( ( radio ) => radio.checked )?.value || '';

					// Validate the email address field for different email option.
					if ( 'different-mail' === sendToEmailValue && ! Helpers.Validator.isEmail( emailAddress.value ) ) {
						emailAddress.nextElementSibling.style.display = 'block';
						emailAddress.closest( '.sui-form-field' ).classList.add( 'sui-form-field-error' );
						return;
					}

					// If there are any error messages displayed, hide it.
					if ( emailAddress.closest( '.sui-form-field' ).classList.contains( 'sui-form-field-error' ) ) {
						emailAddress.closest( '.sui-form-field' ).classList.remove( 'sui-form-field-error' );
						emailAddress.nextElementSibling.style.display = 'none';
					}

					exportModal.querySelector( '.snapshot-export--options__step__content' ).innerHTML = snapshot_messages.export_step_2;
					parentEl.querySelector( '.snapshot-email--tab__content' ).style.display = 'none';

					const filler = parentEl.querySelector( '.snapshot-export--options__wrap' );
					filler.innerHTML = '';
					filler.innerHTML = tmplExportOptions.innerHTML;
					filler.classList.remove( 'd-none' );
					exportModal.querySelector( '.snapshot--send__email' ).classList.remove( 'sui-disabled' );
					exportModal.querySelector( '.snapshot--send__email' ).removeAttribute( 'disabled' );

					e.target.classList.add( 'sui-hidden' );
					e.target.nextElementSibling.classList.remove( 'sui-hidden' );
				} );
			}
		}
	} );
}( jQuery ) );
